import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import { lookupCompanyFromRegistration, isValidRegistrationNumber, isValidIndividualRegistrationNumber, getRegistrationNumberType, shouldTriggerApiCheck } from '../utils/ebarimtHelper';
import { formatDataForStoredProcedure, callStoredProcedure } from '../utils/storedProcedureHelper';
import { convertToMongolian, convertToMongolianImmediate, convertRegistrationNumber, hasConvertibleEnglishLetters } from '../../../shared/utils/mongolianConverter';
import api, { API_BASE_URL, getAuthHeaders } from '../../../shared/utils/api';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import SuccessPopup from '../../../shared/components/SuccessPopup';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';
import Salary from './Salary';

// Import functions from lib
import {
  debounce,
  getNextMonth5th,
  Customer,
  fetchCustomers,
  fetchEmployees,
  getEtaxToken,
  getUserOrgs,
  getOrgShareholder,
  checkTIN,
  checkDirectorRegister,
  checkUserName,
  handleCustomerSelect,
  autoPopulateTaxLogin,
  saveCustomerToDatabase,
  getUserOrgsData,
  populateTinForEdit,
  handleSave as handleSaveLib,
  handleEditSave as handleEditSaveLib,
  handleRowDelete as handleRowDeleteLib,
  handleEditClick as handleEditClickLib,
  handleEditCancel as handleEditCancelLib,
  handleAddTaxLogin as handleAddTaxLoginLib,
  handleAddInsuranceLogin as handleAddInsuranceLoginLib,
  handleEditTaxLogin as handleEditTaxLoginLib,
  handleSaveEditTaxLogin as handleSaveEditTaxLoginLib,
  handleDeleteTaxLogin as handleDeleteTaxLoginLib,
  handleConfirmDeleteTaxLogin as handleConfirmDeleteTaxLoginLib,
  handleEditInsuranceLogin as handleEditInsuranceLoginLib,
  handleSaveEditInsuranceLogin as handleSaveEditInsuranceLoginLib,
  handleDeleteInsuranceLogin as handleDeleteInsuranceLoginLib,
  handleConfirmDeleteInsuranceLogin as handleConfirmDeleteInsuranceLoginLib,
  handleFetchEmployees as handleFetchEmployeesLib,
  handleTabNavigation as handleTabNavigationLib,
  handleFormChange as handleFormChangeLib,
  handleEditChange as handleEditChangeLib,
  validateFormData as validateFormDataLib,
  validateEditFormData as validateEditFormDataLib,
  handleBlurTIN as handleBlurTINLib,
  handleBlurDirectorRegister as handleBlurDirectorRegisterLib,
  handleUserNameValidation as handleUserNameValidationLib,
  handleUserNameValidationEdit as handleUserNameValidationEditLib,
  isRequiredField as isRequiredFieldLib,
  validateRequiredFields as validateRequiredFieldsLib,
  getTabIndex as getTabIndexLib,
  isValidGuid as isValidGuidLib,
  transformCustomerData as transformCustomerDataLib,
  handleCustomerWorkflow as handleCustomerWorkflowLib,
  handleCustomerCodeAutoWorkflow as handleCustomerCodeAutoWorkflowLib,
  normalizeCyrillicText as normalizeCyrillicTextLib,
  extractNumericPart as extractNumericPartLib
} from '../lib';
import { isEntityName } from 'typescript';


const defaultColumns: SmartTableColumn[] = [
  { key: 'Oid', label: 'Дотоод ID' },
  { key: 'CustomerID', label: 'КОД' },
  { key: 'CustomerName', label: 'НЭР' },
  { key: 'ShortName', label: 'Англи нэр' },
  { key: 'IsVatPayer', label: 'НӨАТ төлөгч' },
  { key: 'IsCityPayer', label: 'НХАТ төлөгч' },
  { key: 'TinCode', label: 'ТИН' },
  { key: 'Director', label: 'Захирлын регистр' },
  { key: 'DrFirstname', label: 'Захирлын нэр' },
  { key: 'DrLastname', label: 'Захирлын овог' },
  { key: 'BusinessClassOid', label: 'Бизнес ангилал' },
  { key: 'RegionId', label: 'Аймаг/Хот' },
  { key: 'RegionSubId', label: 'Сум/Дүүрэг' },
  { key: 'Address', label: 'Хаяг' },
  { key: 'Phone', label: 'Утас' },
  { key: 'Mail', label: 'И-мэйл' },
  { key: 'ContractAmount', label: 'Гэрээний дүн' },
  { key: 'Contract_Time', label: 'Гэрээний хугацаа' },
  { key: 'ContractEndDate', label: 'Гэрээ дуусах огноо' },
  { key: 'DigitalSignaturePassword', label: 'ТГҮ нууц үг' },
  { key: 'DigitalCertFilePath', label: 'ТГҮ файл оруулах' },
  { key: 'EBarimtRegistered', label: 'И-Баримт бүртгэл' },
  { key: 'TaxLoginOid', label: 'Татварын нэвтрэх эрх' },
  { key: 'InsuranceLoginId', label: 'НД нэвтрэх эрх' },
  { key: 'Active', label: 'Идэвхтэй' },
  { key: 'CreatedDate', label: 'Бүртгэсэн огноо' },
];

const HariltsagchPage: React.FC = () => {
  const navigate = useNavigate();
  const { userAuth } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [originalCustomers, setOriginalCustomers] = useState<Customer[]>([]); // Store original data with GUIDs
  const [adding, setAdding] = useState(false);
  const [editingRow, setEditingRow] = useState<Customer | null>(null);
  const [editRowData, setEditRowData] = useState<any>({});
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<any>({
    CustomerID: '', 
    CustomerName: '',
    RegionSubId: '',
    Director: '',
    DrFirstname: '',
    DrLastname: '',
    TaxName: '',
    TinCode: '',
    ShortName: '',
    BusinessClassOid: '',   
    Phone: '',
    Mail: '',
    Active: true,
    DigitalSignaturePassword: '',
    DigitalCertFilePath: '',
    EBarimtRegistered: null,
    RegionId: null,
    Address: null,
    ContractAmount: null,
    ContractPeriodType: '',
    Contract_Time: '',
    ContractEndDate: getNextMonth5th(),
    UserName: '',
    IsVatPayer: false,
    IsCityPayer: false,
    InsuranceLoginCode: '',
    InsuranceLoginPassword: '',
    TaxLoginOid: '',
    entId: '',
    pin: '',
    vatpayerRegisteredDate: '',
    CreatedDate: null,
    // Additional fields for tab 2
    Website: '',
    Fax: '',
    BankAccount: '',
    Notes: '',
    AuthorizedRepresentative: '',
    ContactPerson: ''
  });
  const [vatPayer, setVatPayer] = useState(false);
  const [cityPayer, setCityPayer] = useState(false);
  const [regions, setRegions] = useState<any[]>([]);
  const [subregions, setSubregions] = useState<any[]>([]);
  const [taxLogins, setTaxLogins] = useState<any[]>([]);
  const [insuranceLogins, setInsuranceLogins] = useState<any[]>([]);
  const [selectedCertFile, setSelectedCertFile] = useState(null);
  const [businessClasses, setBusinessClasses] = useState<any[]>([]);
  const [showAddTaxLogin, setShowAddTaxLogin] = useState(false);
  const [newTaxLoginOid, setNewTaxLoginOid] = useState('');
  const [registerNoError, setRegisterNoError] = useState('');
  const [isCheckingRegisterNo, setIsCheckingRegisterNo] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isLoadingEBarimt, setIsLoadingEBarimt] = useState(false);
  const [isLoadingDirectorRegister, setIsLoadingDirectorRegister] = useState(false);
  const [directorRegisterWarning, setDirectorRegisterWarning] = useState('');
  const [newTaxLoginPassword, setNewTaxLoginPassword] = useState('');
  const [showAddInsuranceLogin, setShowAddInsuranceLogin] = useState(false);
  const [newInsuranceLoginId, setNewInsuranceLoginId] = useState('');
  const [newInsuranceLoginPassword, setNewInsuranceLoginPassword] = useState('');
  
  // State for edit/delete modals
  const [showEditTaxLogin, setShowEditTaxLogin] = useState(false);
  const [showDeleteTaxLogin, setShowDeleteTaxLogin] = useState(false);
  const [showEditInsuranceLogin, setShowEditInsuranceLogin] = useState(false);
  const [showDeleteInsuranceLogin, setShowDeleteInsuranceLogin] = useState(false);
  const [selectedTaxLogin, setSelectedTaxLogin] = useState<any>(null);
  const [selectedInsuranceLogin, setSelectedInsuranceLogin] = useState<any>(null);
  const [editTaxUsername, setEditTaxUsername] = useState('');
  const [editTaxLoginPassword, setEditTaxLoginPassword] = useState('');
  const [editInsuranceLoginId, setEditInsuranceLoginId] = useState('');
  const [editInsuranceLoginPassword, setEditInsuranceLoginPassword] = useState('');
  const [editVatPayer, setEditVatPayer] = useState(false);
  const [editCityPayer, setEditCityPayer] = useState(false);
  const [ebarimtOriginalInput, setEbarimtOriginalInput] = useState('');

  // Add state for lookup data
  const [allRegions, setAllRegions] = useState<any[]>([]);
  const [allSubregions, setAllSubregions] = useState<any[]>([]);
  const [allBusinessClasses, setAllBusinessClasses] = useState<any[]>([]);
  const [allTaxLogins, setAllTaxLogins] = useState<any[]>([]);
  const [allInsuranceLogins, setAllInsuranceLogins] = useState<any[]>([]);
  const [isAutoPopulatingTaxLogin, setIsAutoPopulatingTaxLogin] = useState(false);
  const [isAutoFetching, setIsAutoFetching] = useState(false);
  const [customerNotFoundMessage, setCustomerNotFoundMessage] = useState('');

  // Success popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Warning popup state
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // Add tab state for the modal
  const [activeTab, setActiveTab] = useState(1);


  // Employee state
  const [employees, setEmployees] = useState<any[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isClearingEmployees, setIsClearingEmployees] = useState(false);
  
  // Salary modal state
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  
  // SmartTable buttons state
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [selectedCustomerOidForSalary, setSelectedCustomerOidForSalary] = useState<string | undefined>(undefined);
  


  // Clear employees state when component mounts or when switching to ADD mode
  // NOTE: Don't clear when editingRow changes - we want to keep employees from API
  useEffect(() => {
    console.log('🔒 SECURITY: useEffect triggered - checking if should clear employees state');
    console.log('🔒 SECURITY: adding:', adding, 'editingRow:', editingRow ? 'exists' : 'null');
    
    // Only clear employees when switching to ADD mode (not when editingRow changes)
    // When editingRow is set, we want to keep employees from API response
    if (adding && !editingRow) {
      console.log('🔒 SECURITY: Clearing employees state - switching to ADD mode');
      setEmployees([]);
      setLoadingEmployees(false);
    } else {
      console.log('🔒 SECURITY: NOT clearing employees state - editingRow exists or not in ADD mode');
    }
  }, [adding]); // Only depend on 'adding', not 'editingRow'

  // Function to transform customer data by replacing GUIDs with names
  const transformCustomerData = (customers: Customer[]) => {
    return customers.map(customer => {
      const transformed: any = { ...customer };
      
      // Format Director field - combine DrFirstname and DrLastname if available
    
      
      // Ensure DrFirstname and DrLastname display properly
      transformed.Director = customer.Director || '-';
      transformed.DrFirstname = customer.DrFirstname || '-';
      transformed.DrLastname = customer.DrLastname || '-';
      
      // Replace BusinessClassOid with name for display
      const businessClassKey = customer.BusinessClassOid;
      if (businessClassKey) {
        const businessClass = allBusinessClasses.find(bc => bc.Oid === businessClassKey);
        transformed.BusinessClassOid = businessClass
          ? (businessClass.BusinessClassName || businessClass.Name || '-')
          : '-';
      } else {
        transformed.BusinessClassOid = '-';
      }
      
      // Human readable VAT payer
      transformed.IsVatPayer = customer.IsVatPayer ? 'Тийм' : 'Үгүй';

      // Replace RegionId with name
      if (customer.RegionId) {
        const region = allRegions.find(r => r.Oid === customer.RegionId);
        if (region) {
          transformed.RegionId = region.Name || region.RegionName || '-';
        } else {
          transformed.RegionId = '-';
        }
      } else {
        transformed.RegionId = '-';
      }
      
      // Replace RegionSubId with name
      if (customer.RegionSubId) {
        const subregion = allSubregions.find(sr => sr.Oid === customer.RegionSubId);
        if (subregion) {
          transformed.RegionSubId = subregion.SubRegionName || subregion.Name || subregion.RegionSubName || '-';
        } else {
          transformed.RegionSubId = '-';
        }
      } else {
        transformed.RegionSubId = '-';
      }
      
      // Replace TaxLoginOid with name
      if (customer.TaxLoginOid) {
        const taxLogin = allTaxLogins.find(tl => tl.Oid === customer.TaxLoginOid);
        if (taxLogin) {
          transformed.TaxLoginOid = taxLogin.TaxUsername;
        }
      }
      
      // Replace InsuranceLoginId with name
      if (customer.InsuranceLoginId) {
        const insuranceLogin = allInsuranceLogins.find(il => il.Oid === customer.InsuranceLoginId);
        if (insuranceLogin) {
          transformed.InsuranceLoginId = insuranceLogin.InsuranceLoginId;
        }
      }
      
      // Combine ContractAmount and ContractPeriodType for display
      if (customer.ContractAmount && customer.ContractPeriodType) {
        transformed.ContractAmount = `${customer.ContractAmount} ${customer.ContractPeriodType}`;
      } else if (customer.ContractAmount) {
        transformed.ContractAmount = customer.ContractAmount.toString();
      }
      
      // Handle Active field display
      if (customer.Active === true) {
        transformed.Active = 'Тийм';
      } else if (customer.Active === false) {
        transformed.Active = 'Үгүй';
      } else {
        transformed.Active = 'Үгүй'; // Default to inactive if null/undefined
      }
      
      return transformed;
    });
  };

  const fieldLabels: { [key: string]: string } = {
    TaxLoginOid: 'Татварын нэвтрэх эрх',
    CustomerID: 'Код',
    CustomerName: 'Нэр',
    ShortName: 'Англи нэр',
    TinCode: 'ТИН',
    DirectorRegister: 'Захирлын регистр ',
    DrFirstname: 'Захирлын нэр',
    DrLastname: 'Захирлын овог',
    BusinessClassOid: 'Бизнес ангилал',
    RegionId: 'Аймаг/Хот',
    RegionSubId: 'Сум/Дүүрэг',
    Address: 'Хаяг',
    Phone: 'Утас',
    Mail: 'И-мэйл',
    ContractAmount: 'Гэрээний дүн',
    ContractEndDate: 'Гэрээ дуусах огноо',
    DigitalSignaturePassword: 'ТГҮ нууц үг',
    DigitalCertFilePath: 'ТГҮ файл оруулах',
    EBarimtRegistered: 'И-Баримт бүртгэл',    
    InsuranceLoginId: 'НД нэвтрэх эрх',
    Active: 'Идэвхтэй',
    CreatedDate: 'Бүртгэсэн огноо',    
    IsVatPayer: 'НӨАТ төлөгч',
    IsCityPayer: 'НХАТ төлөгч',
    UserName: 'App хэрэглэгчийн нэр',
  };

  // Required fields list
  const requiredFields = [
    'CustomerID',      // Код
    'CustomerName',    // Нэр
    'TinCode',         // ТИН
    'DirectorRegister', // Захирлын регистр
    'DrFirstname',     // Захирлын нэр
    'DrLastname',      // Захирлын овог
    'BusinessClassOid', // Бизнес ангилал
    'RegionId',        // Аймаг/Хот
    'RegionSubId',     // Сум/Дүүрэг
    'Phone',           // Утасны дугаар
    'ContractAmount',  // Гэрээний дүн
    'UserName'         // Хэрэглэгчийн нэр
  ];

  // Function to check if field is required
  const isRequiredField = (fieldName: string) => {
    return requiredFields.includes(fieldName);
  };

  // Function to validate required fields
  const validateRequiredFields = () => {
    const missingFields = requiredFields.filter(field => {
      const value = form[field];
      return !value || value.toString().trim() === '';
    });
    return missingFields;
  };

  // Function to render label with asterisk for required fields
  const renderLabel = (fieldName: string) => {
    const isRequired = isRequiredField(fieldName);
    return (
      <label className="block mb-1 text-sm text-gray-700">
        {fieldLabels[fieldName]}
        {isRequired && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
  };

  const includedFields = [

     // Row 7: Татварын нэвтрэх эрх, Код *
     'TaxLoginOid', 'CustomerID',
    // Row 1:  Нэр *, Англи нэр
     'CustomerName', 'TinCode',
    // Row 2: ТИН *, Захирлын регистрийн дугаар *, Захирлын нэр *
     'DirectorRegister', 'DrFirstname', 'DrLastname',
    // Row 3: Захирлын овог *, Бизнес ангилал *, Аймаг/Хот *
    'BusinessClassOid', 'RegionId','RegionSubId',
    // Row 4: Сум/Дүүрэг, Хаяг, Утас *
     'Address', 'Phone','Mail',
    // Row 5: И-мэйл, Гэрээний дүн *, Гэрээ дуусах огноо
     'ContractAmount', 'ContractEndDate','ShortName',
    // Row 6: ТГҮ нууц үг, ТГҮ файл оруулах, И-Баримт бүртгэл
    'DigitalSignaturePassword', 'DigitalCertFilePath', 'EBarimtRegistered',
    // НД нэвтрэх эрх, Арр хэрэглэгчийн нэр *
    , 'InsuranceLoginId', 'UserName', 
    // Row 8: Checkboxes
    'Active'
  ];

  // Explicit list of fields that should be rendered in edit form (same structure as add form)
  const editFormFields = [
    // Row 7: Татварын нэвтрэх эрх, Row 1: Код *,
    'TaxLoginOid',  'CustomerID',
    // Row 1: Код *, Нэр *, Англи нэр
     'CustomerName', 'TinCode',
    // Row 2: ТИН *, Захирлын регистрийн дугаар *, Захирлын нэр *
    'DirectorRegister', 'DrFirstname', 'DrLastname',
    // Row 3: Захирлын овог *, Бизнес ангилал *, Аймаг/Хот *
    'BusinessClassOid', 'RegionId', 'RegionSubId',
    // Row 4: Сум/Дүүрэг, Хаяг, Утас *
    'Address', 'Phone', 'Mail',
    // Row 5: И-мэйл, Гэрээний дүн *, Гэрээ дуусах огноо
    'ContractAmount', 'ContractEndDate', 'ShortName',
    // Row 6: ТГҮ нууц үг, ТГҮ файл оруулах, И-Баримт бүртгэл
    'DigitalSignaturePassword', 'DigitalCertFilePath', 'EBarimtRegistered',
    // Row 7: Татварын нэвтрэх эрх, НД нэвтрэх эрх, Арр хэрэглэгчийн нэр *
     'InsuranceLoginId', 'UserName',
    // Row 8: Additional fields for edit
    'CreatedDate'
  ];



  // Salary modal functions
  const handleOpenSalaryModal = (customerOid: string) => {
    setSelectedCustomerOidForSalary(customerOid);
    setShowSalaryModal(true);
  };

  const handleCloseSalaryModal = () => {
    setShowSalaryModal(false);
    setSelectedCustomerOidForSalary(undefined);
  };

  // SmartTable button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    fetchCustomers();
  };

  const handleFetchEmployees = async (customerId: string, insuranceLoginId: string, isNewCustomer: boolean = false, customerCode?: string, customerName?: string) => {
    try {
      setLoadingEmployees(true);
      
      // Find customerOid from the current customer data
      let customerOid = '';
      if (isNewCustomer && form.Oid) {
        customerOid = form.Oid;
        console.log('📡 handleFetchEmployees: Using form.Oid for new customer:', customerOid);
      } else {
        // For existing customers, find from customers list
        const customer = customers.find(c => c.CustomerID === customerId);
        if (customer && customer.Oid) {
          customerOid = customer.Oid;
          console.log('📡 handleFetchEmployees: Found customerOid from customers list:', customerOid);
        }
      }
      
      // Use the function from employeeSync.ts
      const employees = await handleFetchEmployeesLib(
        customerId,
        insuranceLoginId,
        isNewCustomer,
        customerCode,
        customerName,
        customerOid,
        setEmployees,
        setShowSuccessPopup,
        setSuccessMessage,
        setShowErrorPopup,
        setErrorMessage
      );
      
      console.log('📡 handleFetchEmployees: Completed, employees count:', employees.length);
    } catch (error: any) {
      console.error('❌ handleFetchEmployees: Error:', error);
      setErrorMessage(error.message || 'Ажилчдын мэдээлэл татаж авахад алдаа гарлаа');
      setShowErrorPopup(true);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handler for fetching employees from /api/customer-employees endpoint (for edit modal)
  const handleFetchEmployeesFromEndpoint = async () => {
    if (!editingRow || !editingRow.Oid) {
      console.error('❌ Cannot fetch employees: editingRow or Oid is missing');
      setErrorMessage('Харилцагчийн мэдээлэл олдсонгүй');
      setShowErrorPopup(true);
      return;
    }

    try {
      setLoadingEmployees(true);
      setIsClearingEmployees(false);
      
      const customerOid = editingRow.Oid;
      console.log('🔥 Fetching employees from /api/customer-employees endpoint:', customerOid);
      
      const response = await api.getCustomerEmployees(customerOid);
      
      if (response.success && response.employees) {
        console.log('✅ Employees fetched successfully from customer-employees endpoint:', response.employees.length);
        
        // Format employees data to match expected structure
        const formattedEmployees = response.employees.map((emp: any) => ({
          ...emp,
          Code: emp.Code || '-',
          Name: emp.Name || '-',
          LastName: emp.LastName || '-',
          TIN: emp.TIN || '-',
          InsureNumber: emp.InsureNumber || '-',
          InsureTypeCode: emp.InsureTypeCode || emp.InsureCategoryId || '-',
          OccupationCode: emp.OccupationCode || emp.OccupationCategoryId || '-',
          InsureCategoryId: emp.InsureCategoryId,
          OccupationCategoryId: emp.OccupationCategoryId,
          InsureTypeName: emp.InsureTypeName || null,
          OccupationName: emp.OccupationName || null,
        }));
        
        setEmployees(formattedEmployees);
        setLoadingEmployees(false);
        setSuccessMessage(`Амжилттай! ${formattedEmployees.length} ажилчдын мэдээлэл татагдлаа`);
        setShowSuccessPopup(true);
      } else {
        console.warn('⚠️ No employees found in response');
        setEmployees([]);
        setLoadingEmployees(false);
        setSuccessMessage('Ажилчдын мэдээлэл олдсонгүй');
        setShowSuccessPopup(true);
      }
    } catch (error: any) {
      console.error('❌ Error fetching employees from customer-employees endpoint:', error);
      setErrorMessage(error.message || 'Ажилчдын мэдээлэл татаж авахад алдаа гарлаа');
      setShowErrorPopup(true);
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    if (userAuth?.accountantOid) {
      fetchCustomers().then((customers) => {
        if (customers && customers.length > 0) {
          setCustomers(customers);
          setOriginalCustomers(customers); // originalCustomers state-г шинэчлэх
        }
      }).catch((error) => {
        console.error('❌ Error fetching customers:', error);
        setCustomers([]);
        setOriginalCustomers([]); // Алдаа гарвал хоосон жагсаалт тохируулах
      });
    }
  }, [userAuth?.accountantOid]);

  // Fetch all lookup data on component mount
  useEffect(() => {
    console.log('🔍 useEffect triggered - fetching lookup data');
    console.log('🔍 Component mounted, starting lookup data fetch...');
    
    // Fetch regions
    console.log('🔍 Fetching regions...');
    api.get('/api/regions')
      .then(result => {
        console.log('🔍 Regions API response:', result);
        if (result.success && result.data) {
          console.log('✅ Regions fetched successfully:', result.data.length);
          setAllRegions(result.data);
        } else {
          console.error('❌ Regions API failed:', result);
          setAllRegions([]); // Empty fallback
        }
      })
      .catch(error => {
        console.error('❌ Regions API error:', error);
        setAllRegions([]); // Empty fallback
      });

    // Fetch business classes
    console.log('🔍 Fetching business classes...');
    api.get('/api/BusinessClassOid')
      .then(result => {
        console.log('🔍 Business classes API response:', result);
        if (result.success && result.data) {
          console.log('✅ Business classes fetched successfully:', result.data.length);
          setAllBusinessClasses(result.data);
        } else {
          console.error('❌ Business classes API failed:', result);
          setAllBusinessClasses([]); // Empty fallback
        }
      })
      .catch(error => {
        console.error('❌ Business classes API error:', error);
        setAllBusinessClasses([]); // Empty fallback
      });

    // Fetch tax logins (AccountantOid from Bearer token)
    api.get('/api/tax-login')
      .then(result => {
        if (result.success) setAllTaxLogins(result.data);
      })
      .catch(err => console.error('Tax logins fetch error:', err));

    const currentAccountantOid = localStorage.getItem('accountantOid');

    // Fetch insurance logins for current accountant
    if (currentAccountantOid) {
      api.get(`/api/insurance-login/${currentAccountantOid}`)
        .then(result => {
          if (result.success) setAllInsuranceLogins(result.data);
        });
    }
  }, []);

  // Fetch all subregions once (single request)
  useEffect(() => {
    if (allRegions.length > 0) {
      const fetchAllSubregions = async () => {
        try {
          const result = await api.get('/api/regions/subs-all');
          if (result.success) {
            setAllSubregions(result.data);
          }
        } catch (error) {
          console.error('Error fetching all subregions:', error);
        }
      };
      fetchAllSubregions();
    }
  }, [allRegions]);

  useEffect(() => {
    if (adding) {
      // Use the already fetched data for the add form
      setTaxLogins(allTaxLogins);
      setInsuranceLogins(allInsuranceLogins);
      setRegions(allRegions);
      
      // If we have form data with customer info and tax logins are now loaded, try auto-population
      if (allTaxLogins.length > 0 && form.CustomerID) {
        console.log('🔧 Tax logins loaded, checking if we need to auto-populate tax login...');
        // This will be handled by the existing auto-population logic
      }
    }
  }, [adding, allTaxLogins, allInsuranceLogins, allRegions]);

  useEffect(() => {
    if (form.RegionId) {
      api.get(`/api/customerslist/customer/regions/${form.RegionId}/subs`)
        .then(result => {
          if (result.success) setSubregions(result.data);
        });
    } else {
      setSubregions([]);
    }
  }, [form.RegionId]);

  // Load regions, tax logins, and insurance logins when editing
  useEffect(() => {
    if (editingRow) {
      // Use the already fetched data for the edit form
      setTaxLogins(allTaxLogins);
      setInsuranceLogins(allInsuranceLogins);
      setRegions(allRegions);
      console.log('🔍 Edit form opened - allRegions:', allRegions);
      console.log('🔍 Edit form opened - allRegions length:', allRegions.length);
      console.log('🔍 Edit form opened - editingRow.RegionId:', editingRow.RegionId);
      if (allRegions.length > 0) {
        const matchingRegion = allRegions.find(r => r.Oid === editingRow.RegionId);
        console.log('🔍 Edit form opened - matching region for RegionId:', matchingRegion);
      }
    }
  }, [editingRow, allTaxLogins, allInsuranceLogins, allRegions]);

  // Load subregions when editing and RegionId changes
  useEffect(() => {
    if (editingRow && editRowData.RegionId) {
      api.get(`/api/customerslist/customer/regions/${editRowData.RegionId}/subs`)
        .then(result => {
          if (result.success) setSubregions(result.data);
        });
    }
  }, [editingRow, editRowData.RegionId]);

  useEffect(() => {
    // Use the already fetched business classes data
    console.log('🔍 allBusinessClasses updated:', allBusinessClasses);
    setBusinessClasses(allBusinessClasses);
  }, [allBusinessClasses]);

  useEffect(() => {
    // Use the already fetched regions data
    setRegions(allRegions);
  }, [allRegions]);

  // Debug useEffect to monitor editRowData changes
  useEffect(() => {
    if (editRowData && Object.keys(editRowData).length > 0) {
      console.log('🔥 editRowData changed:', editRowData);
      console.log('🔥 editRowData.InsuranceLoginId:', editRowData.InsuranceLoginId);
      console.log('🔥 editRowData.TaxLoginOid:', editRowData.TaxLoginOid);
    }
  }, [editRowData]);

  // Debounced function to check registration number
  const debouncedCheckTIN = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        
        // Only trigger API check if the registration number has reached the correct length
        const shouldCheck = shouldTriggerApiCheck(value);
        
        if (shouldCheck) {
          timeoutId = setTimeout(() => {
            handleCheckTIN(value);
          }, 500); // Wait 500ms after user stops typing
        }
      };
    }, []),
    []
  );

  // Debounced function to check director registration number
  const debouncedCheckDirectorRegister = React.useCallback(
    React.useMemo(() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        
        // Only trigger API check if the registration number has reached the correct length
        const shouldCheck = shouldTriggerApiCheck(value); // Only check when exactly 10 characters for individuals
        
        if (shouldCheck) {
          timeoutId = setTimeout(() => {
            checkDirectorRegister(value);
          }, 500); // Wait 500ms after user stops typing
        }
      };
    }, []),
    []
  );




  // Force re-render when TaxLoginOid changes
  useEffect(() => {
    console.log('🔄 Form.TaxLoginOid өөрчлөгдлөө:', form.TaxLoginOid);
    if (form.TaxLoginOid) {
      const selectedTaxLogin = taxLogins.find(tl => tl.Oid === form.TaxLoginOid);
      console.log('🔄 Сонгогдсон татварын нэвтрэх эрх:', selectedTaxLogin);
    }
  }, [form.TaxLoginOid, taxLogins]);

  // Auto-trigger fetch information when both fields are filled
  useEffect(() => {
    const customerId = form.CustomerID || '';
    const taxLoginOid = form.TaxLoginOid || '';
    
    // Check if both fields are filled and trigger auto-fetch
    if (customerId && taxLoginOid && customerId.trim() !== '' && taxLoginOid.trim() !== '') {
      console.log('🚀 Auto-triggering fetch information for:', { customerId, taxLoginOid });
      setIsAutoFetching(true);
      
      // Small delay to ensure form state is updated
      setTimeout(async () => {
        try {
          await handleCustomerCodeAutoWorkflow(customerId);
          console.log('✅ Auto-fetch completed successfully');
        } catch (error) {
          console.error('❌ Auto-fetch failed:', error);
        } finally {
          setIsAutoFetching(false);
        }
      }, 500);
    }
  }, [form.CustomerID, form.TaxLoginOid]);

  // Auto-populate UserName from DirectorRegister
  useEffect(() => {
    console.log('🔍 UserName auto-population check:', {
      DirectorRegister: form.DirectorRegister,
      UserName: form.UserName,
      shouldPopulate: form.DirectorRegister && !form.UserName
    });
    
    if (form.DirectorRegister && !form.UserName) {
      console.log('🔧 Auto-populating UserName from DirectorRegister:', form.DirectorRegister);
      setForm(prev => ({ ...prev, UserName: form.DirectorRegister }));
    }
  }, [form.DirectorRegister, form.UserName]);

  // Helper function to normalize Cyrillic text for matching





  // Use checkTIN from lib
  const handleCheckTIN = async (value: string) => {
    console.log('🔥 handleCheckTIN called with CustomerID:', value);
    const regNo = value.trim();
    if (!regNo) {
      console.log('🔥 No regNo, clearing error and returning');
      setRegisterNoError('');
      setIsLoadingEBarimt(false);
      return;
    }

            // Validate registration number format - only show error if user has entered enough characters
        if (regNo.length >= 7 && !shouldTriggerApiCheck(regNo)) {
          setRegisterNoError('Зөвхөн 7 ба түүнээс дээш тэмдэгт оруулна уу');
          setIsLoadingEBarimt(false);
          return;
        }

        // Additional validation for registration number format - only show error if user has finished typing
        const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
        const cleanRegNo = regNo.replace(/\D/g, '');
        
        // Only validate if the user has entered enough characters to potentially be complete
        if (regNo.length >= 7) {
          if (hasLetters) {
            // For individual registration numbers with letters, must be at least 10 characters
            if (regNo.length < 10) {
              setRegisterNoError('Хувь хүний регистрийн дугаар 10 тэмдэгт байх ёстой');
              setIsLoadingEBarimt(false);
              return;
            }
          } else {
            // For company registration numbers (pure numeric), must be exactly 7 digits
            if (cleanRegNo.length !== 7) {
              setRegisterNoError('Байгууллагын регистрийн дугаар 7 оронтой байх ёстой');
              setIsLoadingEBarimt(false);
              return;
            }
          }
        }

    console.log('🔥 Starting validation for regNo:', regNo);
    setIsCheckingRegisterNo(true);
    setIsLoadingEBarimt(true);
    setRegisterNoError('');

    try {
      console.log('🔥 About to call checkTIN with regNo:', regNo);
      // Use checkTIN from lib
      const result = await checkTIN(regNo);
      console.log('🔥 checkTIN result:', result);
      console.log('🔥 checkTIN result type:', typeof result);
      console.log('🔥 checkTIN result keys:', Object.keys(result || {}));
      console.log('🔥 result.success:', result.success);
      console.log('🔥 result.ebarimtData:', result.ebarimtData);
      console.log('🔥 result.exists:', result.exists);
      
      // If EBarimt data is available, populate form
      if (result.success && result.ebarimtData) {
        console.log('🔥 Populating form with EBarimt data:', result.ebarimtData);
        console.log('🔥 About to call setForm with:', {
          CustomerName: result.ebarimtData.userName || '',
          TinCode: result.ebarimtData.tin || '',
          UserName: result.ebarimtData.userName || '',
          IsVatPayer: result.ebarimtData.vatPayer || false,
          IsCityPayer: result.ebarimtData.cityPayer || false,
        

        });
        
        setForm(prev => {
          console.log('🔥 setForm called, previous form:', prev);
          const newForm = {
            ...prev,
            CustomerName: result.ebarimtData.userName || '',
            TinCode: result.ebarimtData.tin || '',
            IsVatPayer: result.ebarimtData.vatPayer || false, // API-аас ирсэн dynamic утга
            IsCityPayer: result.ebarimtData.cityPayer || false,
          };
          console.log('🔥 setForm new form:', newForm);
          return newForm;
        });
        
        // vatPayer checkbox-ийг API-аас ирсэн утгаар шинэчлэх
        setVatPayer(result.ebarimtData.vatPayer || false);
        setCityPayer(result.ebarimtData.cityPayer || false);
        
        setRegisterNoError('');
        setIsCheckingRegisterNo(false);
        setIsLoadingEBarimt(false);
        return;
      }
      
      // Fallback: If no ebarimtData but we have success and no existing customer, try to populate anyway
      if (result.success && !result.exists && !result.ebarimtData) {
        console.log('🔥 No ebarimtData in result, but success=true and exists=false, proceeding to fallback EBarimt lookup');
      }
      
      // Check if existing customer found
      if (result.success && result.exists) {
        console.log('🔥 Existing customer found, isActive:', result.isActive);
        console.log('🔥 Customer data:', result.customerData);
        console.log('🔥 Result object:', result);
        
        if (result.isActive === false || result.isActive === 0) {
          // Allow re-adding inactive customer and populate form with existing data
          console.log('🔥 Found inactive customer, populating form with existing data:', result.customerData);
          setForm(prev => ({
            ...prev,
            Oid: result.customerOid,
            CustomerID: result.customerData.CustomerID || '',
            CustomerName: result.customerData.CustomerName || '',
            ShortName: result.customerData.ShortName || '',
            TinCode: result.customerData.TinCode || '',
            Phone: result.customerData.Phone || '',
            Mail: result.customerData.Mail || '',
            Address: result.customerData.Address || '',
            ContractAmount: result.customerData.ContractAmount || '',
            ContractEndDate: result.customerData.ContractEndDate || '',
            DigitalSignaturePassword: result.customerData.DigitalSignaturePassword || '',
            DigitalCertFilePath: result.customerData.DigitalCertFilePath || '',
            EBarimtRegistered: result.customerData.EBarimtRegistered || false,
            TaxLoginOid: result.customerData.TaxLoginOid || '',
            InsuranceLoginId: result.customerData.InsuranceLoginId || '',
            BusinessClassOid: result.customerData.BusinessClassOid || '',
            RegionId: result.customerData.RegionId || '',
            RegionSubId: result.customerData.RegionSubId || '',
            IsVatPayer: result.customerData.IsVatPayer || false,
            IsCityPayer: result.customerData.IsCityPayer || false,
            Director: result.customerData.Director || '',
            DrFirstname: result.customerData.DrFirstname || '',
            DrLastname: result.customerData.DrLastname || '',
            DirectorRegister: result.customerData.DirectorRegister || result.customerData.Director || '',
            TaxName: result.customerData.TaxName || '',
            UserName: (() => {
              // Priority: UserName > DirectorRegister > Director
              const userName = result.customerData.UserName || 
                              result.customerData.DirectorRegister || 
                              result.customerData.Director || '';
              console.log('🔧 Setting UserName:', {
                UserName: result.customerData.UserName,
                DirectorRegister: result.customerData.DirectorRegister,
                Director: result.customerData.Director,
                final: userName
              });
              return userName;
            })(),
            entId: result.customerData.entId || '',
            pin: result.customerData.pin || '',
            Contract_Time: result.customerData.Contract_Time || '',
            ContractPeriodType: result.customerData.Contract_Time === 'monthly' ? 'сар' :
                              result.customerData.Contract_Time === 'quarterly' ? 'улирал' :
                              result.customerData.Contract_Time === 'yearly' ? 'жил' : '',
            CreatedDate: result.customerData.CreatedDate || null,
            Website: result.customerData.Website || '',
            Fax: result.customerData.Fax || '',
            BankAccount: result.customerData.BankAccount || '',
            Notes: result.customerData.Notes || '',
            AuthorizedRepresentative: result.customerData.AuthorizedRepresentative || '',
            ContactPerson: result.customerData.ContactPerson || '',
            Active: false, // Keep inactive by default when re-adding inactive customer
          }));
          
          // Also update the vatPayer state for the checkbox
          setVatPayer(result.customerData.IsVatPayer || false);
          
          // Auto-populate UserName from DirectorRegister or Director if UserName is empty
          const directorRegister = result.customerData.DirectorRegister || result.customerData.Director || '';
          if (directorRegister && !result.customerData.UserName) {
            console.log('🔧 Auto-populating UserName from DirectorRegister/Director:', directorRegister);
            setForm(prev => ({ ...prev, UserName: directorRegister }));
          }
          
          setRegisterNoError(''); // Clear any previous error
          setIsReactivating(true);
          setIsCheckingRegisterNo(false);
          setIsLoadingEBarimt(false);
          return;
        } else {
          // Active customer exists, show error but populate form with existing data
          console.log('🔥 Active customer exists, showing error but populating form');
          console.log('🔥 Customer isActive:', result.isActive);
          console.log('🔥 Customer data:', result.customerData);
          
          // Populate form with existing customer data
          setForm(prev => ({
            ...prev,
            CustomerName: result.customerData.CustomerName || '',
            TinCode: result.customerData.TinCode || '',
            IsVatPayer: result.customerData.IsVatPayer || false,
            IsCityPayer: result.customerData.IsCityPayer || false,
          }));
          
          setRegisterNoError('Энэ регистрийн дугаар аль хэдийн бүртгэлтэй байна');
          setIsCheckingRegisterNo(false);
          setIsLoadingEBarimt(false);
          return;
        }
      }

      // Proceed to EBarimt API if no active customer exists
      console.log('🔥 No active customer found, proceeding to EBarimt API for regNo:', regNo);
      
      // Use the new eBarimt helper function
      const companyInfo = await lookupCompanyFromRegistration(regNo);
      
      if (!companyInfo) {
        setRegisterNoError('eBarimt API-аас мэдээлэл олдсонгүй');
        setIsCheckingRegisterNo(false);
        setIsLoadingEBarimt(false);
        return;
      }

      setForm(prev => ({
        ...prev,
        CustomerName: companyInfo.name || '',
        TinCode: companyInfo.tin || '',
        RegionSubId: companyInfo.aimagName || '',
        TaxName: companyInfo.vatName || '',
        BusinessClassOid: companyInfo.mainSector || '',
        // Preserve existing TaxLoginOid instead of overwriting with companyInfo.contactName
        TaxLoginOid: (() => {
          console.log('🔧 EBarimt form update - preserving TaxLoginOid:', {
            previous: prev.TaxLoginOid,
            companyContactName: companyInfo.contactName,
            final: prev.TaxLoginOid || companyInfo.contactName || ''
          });
          return prev.TaxLoginOid || companyInfo.contactName || '';
        })(),
        DrFirstname: companyInfo.directorFname || '',
        DrLastname: companyInfo.directorLname || '',
        DirectorRegister: companyInfo.directorRegno || '',
        IsVatPayer: companyInfo.vatPayer || false, // Set VAT Payer from eBarimt data
        IsCityPayer: companyInfo.cityPayer || false,
        Active: true
      }));
      
      // Also update the vatPayer state for the checkbox
      setVatPayer(companyInfo.vatPayer || false);
      
      // Automatically trigger the complete customer workflow to get gdsrNumber and shareHolderName
      console.log('🚀 Auto-triggering complete customer workflow for tax login auto-population...');
      try {
        // Get current accountant's tax login credentials
        const currentAccountantOid = localStorage.getItem('accountantOid');
        if (currentAccountantOid) {
          const credentials = await api.getTaxLogins();
          if (credentials && credentials.data && credentials.data.length > 0) {
            const firstCredential = credentials.data[0];
            console.log('🔍 Using tax credentials for auto-workflow:', firstCredential.TaxUsername);
            
            // Trigger the complete workflow
            console.log('🔍 Before getUserOrgs - localStorage token check:', localStorage.getItem('token') ? 'Present' : 'Missing');
            const workflowResult = await getUserOrgs();
            if (workflowResult && workflowResult.success) {
              console.log('✅ Complete customer workflow completed successfully');
              console.log('✅ Tax login auto-population should now be active');
            } else {
              console.log('⚠️ getUserOrgs failed:', workflowResult);
            }
          }
        }
      } catch (workflowError) {
        console.log('⚠️ Auto-workflow failed, but eBarimt data is still populated:', workflowError);
      }
      
      // Also trigger the complete customer workflow for tax login auto-population
      console.log('🚀 Auto-triggering complete customer workflow for tax login auto-population...');
      setTimeout(() => {
        handleCustomerCodeAutoWorkflow(regNo);
      }, 1000); // Wait 1 second for eBarimt data to settle
      
    } catch (err) {
      console.error('🔥 TIN fetch error:', err);
      console.error('🔥 Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setRegisterNoError('eBarimt API-аас мэдээлэл авахад алдаа гарлаа');
    } finally {
      console.log('🔥 Finally block reached, setting loading states to false');
      setIsCheckingRegisterNo(false);
      setIsLoadingEBarimt(false);
    }
  };

  const handleBlurTIN = async () => {
    await checkTIN(form.CustomerID);
  };

  // Auto-trigger complete customer workflow when customer code is entered
  const handleCustomerCodeAutoWorkflow = async (customerCode: string) => {
    if (!customerCode || customerCode.length < 7) return;
    
    console.log('🚀 Auto-triggering complete customer workflow for code:', customerCode);
    
    try {
      // Check if TaxLoginOid is selected before proceeding
      if (!form.TaxLoginOid || form.TaxLoginOid.trim() === '') {
        console.log('⚠️ TaxLoginOid сонгогдоогүй байна, getUserOrgs дуудахгүй - анхааруулга');
        console.log('⚠️ Эхлээд "Татварын нэвтрэх эрх" талбараас сонголт хийж, дараа нь мэдээлэл татах товчлуур дарна уу');
        setWarningMessage('Эхлээд "Татварын нэвтрэх эрх" талбараас сонголт хийж, дараа нь мэдээлэл татах товчлуур дарна уу');
        setShowWarningPopup(true);
        return;
      }
      
      console.log('✅ TaxLoginOid сонгогдсон байна:', form.TaxLoginOid);
      
      // Get current accountant's tax login credentials
      const currentAccountantOid = localStorage.getItem('accountantOid');
      if (currentAccountantOid) {
        const credentials = await api.getTaxLogins();
        if (credentials && credentials.data && credentials.data.length > 0) {
          const firstCredential = credentials.data[0];
          console.log('🔍 Auto-workflow using tax credentials:', firstCredential.TaxLoginOid);
          
          // Trigger the complete workflow
          console.log('🔍 Before getUserOrgs - localStorage token check:', localStorage.getItem('token') ? 'Present' : 'Missing');
          const workflowResult = await getUserOrgs();
          if (workflowResult && workflowResult.success) {
            console.log('✅ Auto-workflow completed successfully for customer:', customerCode);
            console.log('✅ Form should now be fully populated with tax login auto-selected');
          } else {
            console.log('⚠️ getUserOrgs failed for customer:', customerCode, workflowResult);
          }
        }
      }
    } catch (workflowError) {
      console.log('⚠️ Auto-workflow failed for customer:', customerCode, workflowError);
    }
  };

  const checkDirectorRegister = async (value: string) => {
    console.log('🔥 checkDirectorRegister called with DirectorRegister:', value);
    const regNo = value.trim();
    if (!regNo) {
      console.log('🔥 No DirectorRegister regNo, returning');
      return;
    }

    // Validate that this is an individual registration number (must contain letters and be at least 10 characters)
    const hasLetters = /[A-Za-zА-Яа-я]/.test(regNo);
    
    // Only show warnings if user has entered enough characters to potentially be complete
    if (regNo.length >= 7) {
      if (!hasLetters) {
        setDirectorRegisterWarning('Зөвхөн хувь хүний регистрийн дугаар оруулна уу (үсэгтэй байх ёстой).');
        setIsLoadingEBarimt(false);
        return;
      } else if (regNo.length < 10) {
        setDirectorRegisterWarning('Хувь хүний регистрийн дугаар 10 тэмдэгт байх ёстой');
        setIsLoadingEBarimt(false);
        return;
      } else {
        setDirectorRegisterWarning(''); // Clear warning if valid
      }
    } else {
      // Clear any existing warnings if user is still typing
      setDirectorRegisterWarning('');
    }

    setIsLoadingEBarimt(true);

    try {
      // First check if this RegisterNo already exists in our database
      const existingResult = await api.get(`/api/customerslist/check-register/${regNo}`);
      
      console.log('Director RegisterNo check result:', existingResult);
      
      if (existingResult.exists && (!form.Oid || existingResult.customerOid !== form.Oid)) {
        // Check if the existing customer is inactive
        if (existingResult.isActive === false || existingResult.isActive === 0) {
          // Allow re-adding inactive customer and populate form with existing data
          console.log('Found inactive customer via director register, populating form:', existingResult.customerData);
          setForm(prev => ({
            ...prev,
            Oid: existingResult.customerOid,
            CustomerID: existingResult.customerData.CustomerID || '',
            CustomerName: existingResult.customerData.CustomerName || '',
            ShortName: existingResult.customerData.ShortName || '',
            TinCode: existingResult.customerData.TinCode || '',
            Phone: existingResult.customerData.Phone || '',
            Mail: existingResult.customerData.Mail || '',
            Address: existingResult.customerData.Address || '',
            ContractAmount: existingResult.customerData.ContractAmount || '',
            ContractEndDate: existingResult.customerData.ContractEndDate || '',
            DigitalSignaturePassword: existingResult.customerData.DigitalSignaturePassword || '',
            DigitalCertFilePath: existingResult.customerData.DigitalCertFilePath || '',
            EBarimtRegistered: existingResult.customerData.EBarimtRegistered || false,
            TaxLoginOid: existingResult.customerData.TaxLoginOid || '',
            InsuranceLoginId: existingResult.customerData.InsuranceLoginId || '',
            BusinessClassOid: existingResult.customerData.BusinessClassOid || '',
            RegionId: existingResult.customerData.RegionId || '',
            RegionSubId: existingResult.customerData.RegionSubId || '',
            IsVatPayer: existingResult.customerData.IsVatPayer || false,
            IsCityPayer: existingResult.customerData.IsCityPayer || false,
            Active: true, // Set to active when re-adding
          }));
          setRegisterNoError(''); // Clear any previous error
          setIsReactivating(true);
          setIsLoadingEBarimt(false);
          return;
        } else {
          // Active customer exists, show error
          console.log('Active customer exists via director register, showing error');
          setRegisterNoError('Энэ регистрийн дугаар аль хэдийн бүртгэлтэй байна');
          setIsLoadingEBarimt(false);
          return;
        }
      }

      // Only proceed to EBarimt API if no active customer exists
      console.log('🔥 No active customer found, proceeding to EBarimt API for director register:', regNo);
      
      // Use the new eBarimt helper function
      const companyInfo = await lookupCompanyFromRegistration(regNo);
      
      if (!companyInfo) {
        console.log('🔥 No company info found for director register');
        setIsLoadingEBarimt(false);
        return;
      }

      // Only populate director information, NOT TIN
      if (companyInfo.name) {
        const nameParts = companyInfo.name.split(' ').filter(part => part.trim());
        let lastName = '';
        let firstName = '';
        if (nameParts.length === 1) {
          // Single name - put it in last name field
          lastName = nameParts[0];
          firstName = '';
        } else if (nameParts.length >= 2) {
          // Multiple names - first is last name, rest is first name
          lastName = nameParts[0];
          firstName = nameParts.slice(1).join(' ');
        }
        
        setForm(prev => ({
          ...prev,
          DrFirstname: companyInfo.directorFname || firstName,
          DrLastname: companyInfo.directorLname || lastName,
          DirectorRegister: companyInfo.directorRegno || regNo,
        }));
      }
      
      setIsLoadingEBarimt(false);
    } catch (err) {
      console.error('Director register fetch error:', err);
      setIsLoadingEBarimt(false);
    }
  };

  const handleBlurDirectorRegister = async () => {
    await checkDirectorRegister(form.DirectorRegister);
  };

  // Function to check UserName availability immediately
  const checkUserName = async (userName: string) => {
    if (!userName || userName.trim().length === 0) return;
    
    try {
      console.log('🔥 Checking UserName availability immediately:', userName);
      const existingUserResult = await api.get(`/users/check-register-accountant/${userName}`);
      
      if (existingUserResult.exists) {
        setErrorMessage(`Энэ хэрэглэгчийн нэр аль хэдийн бүртгэлтэй байна: ${userName}`);
        setShowErrorPopup(true);
        setForm(prev => ({ ...prev, UserName: '' }));
      } else {
        console.log('🔥 UserName is available:', userName);
      }
    } catch (err) {
      console.error('🔥 Error checking UserName availability:', err);
    }
  };

  // Function to check UserName availability immediately for edit form
  const checkUserNameEdit = async (userName: string) => {
    if (!userName || userName.trim().length === 0) return;
    
    try {
      console.log('🔥 Checking UserName availability immediately in edit:', userName);
      const existingUserResult = await api.get(`/users/check-register-accountant/${userName}`);
      
      if (existingUserResult.exists) {
        setErrorMessage(`Энэ хэрэглэгчийн нэр аль хэдийн бүртгэлтэй байна: ${userName}`);
        setShowErrorPopup(true);
        setEditRowData(prev => ({ ...prev, UserName: '' }));
      } else {
        console.log('🔥 UserName is available in edit:', userName);
      }
    } catch (err) {
      console.error('🔥 Error checking UserName availability in edit:', err);
    }
  };

  // Debounced function for DirectorRegister in edit form
  const checkDirectorRegisterEdit = useCallback(
    debounce(async (value: string) => {
      console.log('🔥 checkDirectorRegisterEdit called with DirectorRegister:', value);
      const regNo = value.trim();
      if (!regNo) {
        console.log('🔥 No DirectorRegister regNo, returning');
        setIsLoadingDirectorRegister(false);
        return;
      }

      // Validate that this is an individual registration number (must contain letters)
      // Only show warnings if user has entered enough characters to potentially be complete
      if (regNo.length >= 7) {
        if (!isValidIndividualRegistrationNumber(regNo)) {
          console.log('Invalid director registration number format in edit. Only individual registration numbers (with letters) are allowed.');
          setDirectorRegisterWarning('Зөвхөн хувь хүний регистрийн дугаар оруулна уу (үсэгтэй байх ёстой).');
          setIsLoadingDirectorRegister(false);
          return;
        } else {
          setDirectorRegisterWarning(''); // Clear warning if valid
        }
      } else {
        // Clear any existing warnings if user is still typing
        setDirectorRegisterWarning('');
      }

      setIsLoadingDirectorRegister(true);

      try {
        // Use the new eBarimt helper function to get director info
        console.log('🔥 Edit mode: Fetching director info from eBarimt API...');
        const companyInfo = await lookupCompanyFromRegistration(regNo);
        
        if (!companyInfo) {
          console.log('🔥 No company info found for director register in edit mode');
          setIsLoadingDirectorRegister(false);
          return;
        }

        // Only populate director information, NOT TIN
        if (companyInfo.name) {
          const nameParts = companyInfo.name.split(' ').filter(part => part.trim());
          let lastName = '';
          let firstName = '';

          if (nameParts.length === 1) {
            // Single name - put it in last name field
            lastName = nameParts[0];
            firstName = '';
          } else if (nameParts.length >= 2) {
            // Multiple names - first is last name, rest is first name
            lastName = nameParts[0];
            firstName = nameParts.slice(1).join(' ');
          }

          console.log('🔥 Edit mode EBarimt director data:', { 
            name: companyInfo.name, 
            lastName, 
            firstName,
            directorFname: companyInfo.directorFname,
            directorLname: companyInfo.directorLname,
            directorRegno: companyInfo.directorRegno
          });

          setEditRowData(prev => ({
            ...prev,
            DrFirstname: companyInfo.directorFname || firstName,
            DrLastname: companyInfo.directorLname || lastName,
            DirectorRegister: companyInfo.directorRegno || regNo,
            UserName: regNo, // Automatically set UserName to the Director Register Number
          }));
        }
        
        setIsLoadingDirectorRegister(false);
      } catch (err) {
        console.error('Edit mode director register fetch error:', err);
        // Don't show alert for API errors, just log them
        console.log('🔥 API error occurred, continuing without database check');
        setIsLoadingDirectorRegister(false);
      }
    }, 500),
    []
  );

  // Function to populate TIN for edit form
  const populateTinForEdit = async (customerId: string) => {
    console.log('🔥 populateTinForEdit called with CustomerID:', customerId);
    
    if (!customerId || !isValidRegistrationNumber(customerId)) {
      console.log('🔥 Invalid CustomerID for TIN population');
      return;
    }
    
    try {
      console.log('🔥 Fetching TIN from eBarimt API for edit...');
      const companyInfo = await lookupCompanyFromRegistration(customerId);
      
      if (companyInfo && companyInfo.tin) {
        console.log('🔥 TIN found for edit:', companyInfo.tin);
        setEditRowData(prev => ({
          ...prev,
          TinCode: companyInfo.tin,
          CustomerName: companyInfo.name || prev.CustomerName,
          RegionSubId: companyInfo.aimagName || prev.RegionSubId,
          DrFirstname: companyInfo.directorFname || prev.DrFirstname,
          DrLastname: companyInfo.directorLname || prev.DrLastname,
          DirectorRegister: companyInfo.directorRegno || prev.DirectorRegister,
        }));
      } else {
        console.log('🔥 No TIN found for CustomerID:', customerId);
      }
    } catch (error) {
      console.error('🔥 Error populating TIN for edit:', error);
    }
  };

  const handleSave = async () => {
    // Check for RegisterNo error first
    if (registerNoError) {
      setErrorMessage('Регистрийн дугаар алдаатай байна: ' + registerNoError);
      setShowErrorPopup(true);
      return;
    }

    // Validate required fields first
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(field => fieldLabels[field]).join(', ');
      setErrorMessage(`Дараах талбаруудыг бөглөнө үү: ${missingFieldNames}`);
      setShowErrorPopup(true);
      return;
    }

    let digitalCertFilePath = form.DigitalCertFilePath || '';

    if (selectedCertFile) {
      const formData = new FormData();
      formData.append('DigitalCertFile', selectedCertFile);
      try {
        const response = await api.uploadCert(formData);
        const data = await response.json();
        if (data.success && data.file && data.file.filename) {
          digitalCertFilePath = data.file.filename;
        } else {
          setErrorMessage('File upload failed: ' + (data.error || 'Unknown error'));
          setShowErrorPopup(true);
          return;
        }
      } catch (err) {
        setErrorMessage('File upload failed: ' + err.message);
        setShowErrorPopup(true);
        return;
      }
    }

    // Debug log for TaxLoginOid
    console.log('TaxLoginOid being sent:', form.TaxLoginOid);

    const customerData = {
      ...form,
      DigitalCertFilePath: digitalCertFilePath,
      IsVatPayer: vatPayer,
    };

    // Debug log to see what's being sent
    console.log('🔥 Saving customer data:', customerData);
    console.log('🔥 UserName value:', customerData.UserName);

    // UserName validation is now handled immediately when typing, so we don't need to validate here again

    try {
      // Format data for stored procedure
      const storedProcedureData = formatDataForStoredProcedure(customerData, true);
      
      // Call stored procedure
      const result = await callStoredProcedure(storedProcedureData);
      
      if (result.success) {
        // Store the saved customer data for auto-switch to edit mode
        const savedCustomerData = {
          ...customerData,
          Oid: result.customerOid || result.data?.Oid // Get the Oid from the response
        };
        
       
        
    
        
        // Don't close the modal yet - we'll switch to edit mode instead
        // setAdding(false);
        // Clear employees state to prevent security breach
        setEmployees([]);
        setLoadingEmployees(false);
        // Don't reset the form yet - let edit mode handle it
        // setForm({...});
        // setVatPayer(false);
        // setSelectedCertFile(null);
        
        // Fetch updated customers list and then auto-switch to edit mode
        fetchCustomers().then(() => {
          // Find the newly saved customer in the updated list
          const newlySavedCustomer = originalCustomers.find(c => 
            c.CustomerID === savedCustomerData.CustomerID && 
            c.CustomerName === savedCustomerData.CustomerName
          );
          
          if (newlySavedCustomer) {
            console.log('🔥 Auto-switch: Found newly saved customer:', newlySavedCustomer);
            // Auto-switch to edit mode for the newly saved customer
            setTimeout(() => {
              console.log('🔥 Auto-switch: Starting auto-switch to edit mode...');
              // Close add modal and open edit modal
              setAdding(false);
              handleEditClick(newlySavedCustomer);
            }, 1000); // Increased delay to ensure UI updates properly
          } else {
            console.log('🔥 Auto-switch: Could not find newly saved customer in list');
            console.log('🔥 Auto-switch: savedCustomerData:', savedCustomerData);
            console.log('🔥 Auto-switch: originalCustomers:', originalCustomers);
            // If we can't find the customer, just close the add modal
            setAdding(false);
          }
        }).catch((error) => {
          console.error('Error fetching customers after save:', error);
          // Close the add modal even if fetch fails
          setAdding(false);
          // Show success message even if auto-switch fails
          setSuccessMessage('Амжилттай хадгаллаа');
          setShowSuccessPopup(true);
        });
        
        // Show success message immediately
        setSuccessMessage('Амжилттай хадгаллаа');
        setShowSuccessPopup(true);
      } else {
        setErrorMessage('Алдаа гарлаа: ' + (result.message || 'Unknown error'));
        setShowErrorPopup(true);
      }
    } catch (err) {
      setErrorMessage('Failed to add customer: ' + err.message);
      setShowErrorPopup(true);
    }
  };

  const handleEditClick = async (customer: Customer) => {
    console.log('🔒 SECURITY: Clearing employees state for new customer edit');
    console.log('🔒 SECURITY: Previous employees count:', employees.length);
    // Clear employees state to prevent security breach - show previous customer's data
    setIsClearingEmployees(true);
    setEmployees([]);
    setLoadingEmployees(true); // Set loading to true while fetching
    console.log('🔒 SECURITY: Employees state cleared');
    
    // Don't clear again after delay - we'll set it from API response
    
    // The customer parameter contains the transformed data (with names instead of GUIDs)
    // We need to find the original customer data with GUIDs from the originalCustomers array
    console.log('🔥 Customer data received for edit:', customer);
    console.log('🔥 Customer Oid:', customer.Oid);
    
    // 🔥 CustomerOid-оор customer-with-employees endpoint дуудаж мэдээлэл авах
    if (customer.Oid) {
      try {
        console.log('🔥 Fetching customer and employees data from API:', customer.Oid);
        const response = await api.getCustomerWithEmployees(customer.Oid);
        
        if (response.success && response.data) {
          console.log('✅ Customer and employees data fetched successfully:', response.data);
          console.log('✅ Full API response:', JSON.stringify(response.data, null, 2));
          const { customer: fetchedCustomer, employees: fetchedEmployees } = response.data;
          
          console.log('🔥 Fetched customer:', fetchedCustomer);
          console.log('🔥 Fetched employees:', fetchedEmployees);
          console.log('🔥 Fetched employees type:', typeof fetchedEmployees);
          console.log('🔥 Fetched employees isArray:', Array.isArray(fetchedEmployees));
          console.log('🔥 Fetched employees length:', fetchedEmployees?.length || 0);
          
          // Fetched customer мэдээллийг ашиглах
          if (fetchedCustomer) {
            console.log('🔥 Using fetched customer data:', fetchedCustomer);
            
            // empID is already in fetchedCustomer from customer-with-employees endpoint (SELECT * FROM Customers)
            if (fetchedCustomer.empID !== null && fetchedCustomer.empID !== undefined) {
              fetchedCustomer.empId = fetchedCustomer.empID;
              console.log('🔥 Set empId from fetchedCustomer.empID:', fetchedCustomer.empID);
            } else {
              console.log('⚠️ No empID found in fetchedCustomer');
            }
            
            // Set employees if available
            if (fetchedEmployees && Array.isArray(fetchedEmployees)) {
              console.log('✅ Setting employees from API:', fetchedEmployees.length);
              console.log('✅ Employees data from API (full):', fetchedEmployees);
              console.log('✅ Employees data from API (first 3):', fetchedEmployees.slice(0, 3));
              
              // Format employees data to match expected structure
              console.log('🔄 Formatting employees data...');
              console.log('🔄 Raw fetchedEmployees:', fetchedEmployees);
              console.log('🔄 Raw fetchedEmployees length:', fetchedEmployees.length);
              
              const formattedEmployees = fetchedEmployees.map((emp: any, index: number) => {
                const formatted = {
                  ...emp,
                  // Ensure all required fields are present
                  Code: emp.Code || '-',
                  Name: emp.Name || '-',
                  LastName: emp.LastName || '-',
                  TIN: emp.TIN || '-',
                  InsureNumber: emp.InsureNumber || '-',
                  // Use InsureTypeCode from JOIN (Code from InsureCategory table)
                  // If not available, fallback to InsureCategoryId GUID
                  InsureTypeCode: emp.InsureTypeCode || emp.InsureCategoryId || '-',
                  // Use OccupationCode from JOIN (Code from OccupationCategory table)
                  // If not available, fallback to OccupationCategoryId GUID
                  OccupationCode: emp.OccupationCode || emp.OccupationCategoryId || '-',
                  // Keep original IDs for reference
                  InsureCategoryId: emp.InsureCategoryId,
                  OccupationCategoryId: emp.OccupationCategoryId,
                  // Keep names for reference (optional)
                  InsureTypeName: emp.InsureTypeName || null,
                  OccupationName: emp.OccupationName || null,
                };
                
                if (index < 3) {
                  console.log(`🔄 Formatted employee ${index}:`, {
                    Code: formatted.Code,
                    Name: formatted.Name,
                    InsureTypeCode: formatted.InsureTypeCode,
                    OccupationCode: formatted.OccupationCode,
                    InsureCategoryId: formatted.InsureCategoryId,
                    OccupationCategoryId: formatted.OccupationCategoryId
                  });
                }
                
                return formatted;
              });
              
              console.log('✅ Formatted employees (full):', formattedEmployees);
              console.log('✅ Formatted employees length:', formattedEmployees.length);
              console.log('✅ Formatted employees (first 3):', formattedEmployees.slice(0, 3));
              console.log('✅ Formatted employees (last 3):', formattedEmployees.slice(-3));
              console.log('✅ Setting employees state with', formattedEmployees.length, 'employees');
              
              // Set employees state FIRST before continueEditProcess
              console.log('🔄 Calling setEmployees with formattedEmployees...');
              console.log('🔄 Before setEmployees - current employees state length:', employees.length);
              setEmployees(formattedEmployees);
              setLoadingEmployees(false);
              setIsClearingEmployees(false);
              
              console.log('✅ setEmployees called, formattedEmployees.length:', formattedEmployees.length);
              console.log('✅ setLoadingEmployees(false) called');
              console.log('✅ setIsClearingEmployees(false) called');
              console.log('✅ Employees state should be set now, now calling continueEditProcess');
            } else {
              console.log('⚠️ No employees found in API response');
              setEmployees([]);
              setLoadingEmployees(false);
              setIsClearingEmployees(false);
            }
            
            // Update originalCustomers array with fetched customer
            const updatedOriginalCustomers = [...originalCustomers];
            const existingIndex = updatedOriginalCustomers.findIndex(c => c.Oid === fetchedCustomer.Oid);
            if (existingIndex >= 0) {
              updatedOriginalCustomers[existingIndex] = fetchedCustomer;
            } else {
              updatedOriginalCustomers.push(fetchedCustomer);
            }
            setOriginalCustomers(updatedOriginalCustomers);
            
            // Ensure TaxLoginOid is set in editRowData for dropdown display
            console.log('🔥 Setting TaxLoginOid from fetched customer:', fetchedCustomer.TaxLoginOid);
            
            // Continue with edit process using fetched customer
            // Use setTimeout to ensure employees state is set before continueEditProcess
            setTimeout(() => {
              console.log('🔥 Calling continueEditProcess after employees state is set');
              continueEditProcess(fetchedCustomer, fetchedCustomer);
            }, 50);
            return;
          }
        } else {
          console.warn('⚠️ Failed to fetch customer with employees, falling back to original method');
        }
      } catch (error) {
        console.error('❌ Error fetching customer with employees:', error);
        console.warn('⚠️ Falling back to original edit method');
      }
    }
    
    // Find the original customer data with GUIDs (fallback to original method)
    console.log('🔥 originalCustomers array length:', originalCustomers.length);
    console.log('🔥 originalCustomers array:', originalCustomers);
    console.log('🔥 Looking for customer with Oid:', customer.Oid);
    console.log('🔥 Available Oids in originalCustomers:', originalCustomers.map(c => ({ oid: c.Oid, customerId: c.CustomerID, customerName: c.CustomerName })));
    
    const originalCustomer = originalCustomers.find(c => c.Oid === customer.Oid);
    if (!originalCustomer) {
      console.error('Original customer not found for Oid:', customer.Oid);
      console.error('Available Oids in originalCustomers:', originalCustomers.map(c => c.Oid));
      
      // Fallback: if originalCustomers is empty, try to fetch fresh data
      if (originalCustomers.length === 0) {
        console.log('🔥 originalCustomers is empty, fetching fresh data...');
        fetchCustomers().then((customers) => {
          if (customers && customers.length > 0) {
            setOriginalCustomers(customers); // originalCustomers state-г шинэчлэх
            // Retry the edit after data is fetched
            setTimeout(() => {
              const retryOriginalCustomer = customers.find(c => c.Oid === customer.Oid);
              if (retryOriginalCustomer) {
                console.log('🔥 Found original customer on retry:', retryOriginalCustomer);
                // Continue with the edit process
                continueEditProcess(retryOriginalCustomer, customer);
              } else {
                console.error('🔥 Still cannot find original customer after retry');
              }
            }, 100);
          }
        });
        return;
      }
      return;
    }
    
    console.log('🔥 Original customer InsuranceLoginId:', originalCustomer.InsuranceLoginId);
    console.log('🔥 Original customer InsuranceLoginId type:', typeof originalCustomer.InsuranceLoginId);
    console.log('🔥 Original customer InsuranceLoginId is null?', originalCustomer.InsuranceLoginId === null);
    console.log('🔥 Original customer InsuranceLoginId is undefined?', originalCustomer.InsuranceLoginId === undefined);
    console.log('🔥 Transformed customer InsuranceLoginId:', customer.InsuranceLoginId);
    console.log('🔥 Original customer TaxLoginOid:', originalCustomer.TaxLoginOid);
    console.log('🔥 Transformed customer TaxLoginOid:', customer.TaxLoginOid);
    console.log('🔥 Is originalCustomer.InsuranceLoginId a GUID?', /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(originalCustomer.InsuranceLoginId));
    console.log('🔥 Is originalCustomer.TaxLoginOid a GUID?', /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(originalCustomer.TaxLoginOid));
    console.log('🔥 InsuranceLogins available:', insuranceLogins.map(il => ({ oid: il.Oid, name: il.InsuranceLoginId })));
    console.log('🔥 TaxLogins available:', taxLogins.map(tl => ({ oid: tl.Oid, name: tl.TaxLoginOid })));
    
    console.log('🔥 Original customer data for edit:', originalCustomer);
    console.log('🔥 Transformed customer data received:', customer);
    console.log('🔥 UserName in originalCustomer:', originalCustomer.UserName);
    
    // Continue with the edit process
    continueEditProcess(originalCustomer, customer);
  };

  // Helper function to continue the edit process
  const continueEditProcess = (originalCustomer: Customer, customer: Customer) => {
    console.log('🔥 continueEditProcess - originalCustomer.InsuranceLoginId:', originalCustomer.InsuranceLoginId);
    console.log('🔥 continueEditProcess - originalCustomer.TaxLoginOid:', originalCustomer.TaxLoginOid);
    console.log('🔥 continueEditProcess - Current employees state length:', employees.length);
    console.log('🔥 continueEditProcess - isClearingEmployees:', isClearingEmployees);
    
    // IMPORTANT: Don't clear employees state here - it should already be set from API
    // Only clear if we're starting a new edit (not from API fetch)
    
    setEditingRow(originalCustomer);
    
    // Use the original customer data (with GUIDs) for editing
    const editData = { ...originalCustomer };
    
    console.log('🔥 editData after spread - InsuranceLoginId:', editData.InsuranceLoginId);
    console.log('🔥 editData after spread - TaxLoginOid:', editData.TaxLoginOid);
    
    // Ensure InsuranceLoginId and TaxLogin Id are GUIDs, not transformed names
    console.log('🔥 Original InsuranceLoginId from database:', originalCustomer.InsuranceLoginId);
    console.log('🔥 Original TaxLoginOid from database:', originalCustomer.TaxLoginOid);
    
    // 🔧 FIXED: InsuranceLoginId now stores GUIDs directly in database
    if (originalCustomer.InsuranceLoginId) {
      console.log('🔥 InsuranceLoginId from database (should be GUID):', originalCustomer.InsuranceLoginId);
      
      // Check if it's already a GUID (new format) or string (old format)
      if (isValidGuidLib(originalCustomer.InsuranceLoginId)) {
        console.log('🔥 InsuranceLoginId is already a GUID, using directly');
        editData.InsuranceLoginId = originalCustomer.InsuranceLoginId;
      } else {
        console.log('🔥 InsuranceLoginId is string, converting to GUID:', originalCustomer.InsuranceLoginId);
        console.log('🔥 InsuranceLogins available:', insuranceLogins.map(il => ({ oid: il.Oid, name: il.InsuranceLoginId })));
        console.log('🔥 AllInsuranceLogins available:', allInsuranceLogins.map(il => ({ oid: il.Oid, name: il.InsuranceLoginId })));
        
        // Try to find in insuranceLogins first, then fallback to allInsuranceLogins
        let insuranceLogin = insuranceLogins.find(il => il.InsuranceLoginId === originalCustomer.InsuranceLoginId);
        if (!insuranceLogin && allInsuranceLogins.length > 0) {
          console.log('🔥 Not found in insuranceLogins, trying allInsuranceLogins...');
          insuranceLogin = allInsuranceLogins.find(il => il.InsuranceLoginId === originalCustomer.InsuranceLoginId);
        }
        
        console.log('🔥 Found insurance login:', insuranceLogin);
        editData.InsuranceLoginId = insuranceLogin ? insuranceLogin.Oid : null;
        console.log('🔥 Converted InsuranceLoginId:', editData.InsuranceLoginId);
      }
    } else {
      editData.InsuranceLoginId = null;
    }
    
    if (originalCustomer.TaxLoginOid) {
      console.log('🔥 Processing TaxLoginOid:', originalCustomer.TaxLoginOid);
      console.log('🔥 TaxLogins available:', taxLogins.map(tl => ({ oid: tl.Oid, name: tl.TaxUsername })));
      console.log('🔥 AllTaxLogins available:', allTaxLogins.map(tl => ({ oid: tl.Oid, name: tl.TaxUsername })));
      
      // Check if TaxLoginOid is already a GUID
      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(originalCustomer.TaxLoginOid);
      
      if (isGuid) {
        // Already a GUID, check if it exists in taxLogins
        console.log('🔥 TaxLoginOid is already a GUID, checking if exists in taxLogins...');
        let taxLogin = taxLogins.find(tl => tl.Oid === originalCustomer.TaxLoginOid);
        if (!taxLogin && allTaxLogins.length > 0) {
          console.log('🔥 Not found in taxLogins, trying allTaxLogins...');
          taxLogin = allTaxLogins.find(tl => tl.Oid === originalCustomer.TaxLoginOid);
        }
        
        if (taxLogin) {
          console.log('✅ TaxLoginOid found in taxLogins, using directly:', taxLogin.Oid);
          editData.TaxLoginOid = taxLogin.Oid;
        } else {
          console.log('⚠️ TaxLoginOid GUID not found in taxLogins, using original value:', originalCustomer.TaxLoginOid);
          editData.TaxLoginOid = originalCustomer.TaxLoginOid;
        }
      } else {
        // Not a GUID, try to find by TaxUsername
        console.log('🔥 TaxLoginOid is not a GUID, trying to find by TaxUsername:', originalCustomer.TaxLoginOid);
        let taxLogin = taxLogins.find(tl => tl.TaxUsername === originalCustomer.TaxLoginOid);
        if (!taxLogin && allTaxLogins.length > 0) {
          console.log('🔥 Not found in taxLogins, trying allTaxLogins...');
          taxLogin = allTaxLogins.find(tl => tl.TaxUsername === originalCustomer.TaxLoginOid);
        }
        
        if (taxLogin) {
          console.log('✅ TaxLogin found by TaxUsername, using Oid:', taxLogin.Oid);
          editData.TaxLoginOid = taxLogin.Oid;
        } else {
          console.log('⚠️ TaxLogin not found by TaxUsername, keeping original value:', originalCustomer.TaxLoginOid);
          editData.TaxLoginOid = originalCustomer.TaxLoginOid;
        }
      }
      
      console.log('🔥 Final TaxLoginOid in editData:', editData.TaxLoginOid);
    } else {
      editData.TaxLoginOid = null;
      console.log('⚠️ TaxLoginOid is null or undefined');
    }
    
    console.log('🔥 editData after forcing GUIDs - InsuranceLoginId:', editData.InsuranceLoginId);
    console.log('🔥 editData after forcing GUIDs - TaxLoginOid:', editData.TaxLoginOid);
    
    // Ensure Active field is properly set
    if (editData.Active === null || editData.Active === undefined) {
      editData.Active = true; // Default to active if not set
    }
    
    // Ensure empId is set from originalCustomer (fetched from customer-employees endpoint)
    if (originalCustomer.empId !== null && originalCustomer.empId !== undefined) {
      editData.empId = originalCustomer.empId;
      console.log('🔥 Set empId in editData from originalCustomer:', originalCustomer.empId);
    } else {
      // If empId is not in originalCustomer, set to empty string (will be converted to null in handleEditSave)
      editData.empId = '';
      console.log('⚠️ empId not found in originalCustomer, setting to empty string');
    }
    
    // Ensure all required fields have proper values
    if (editData.DrFirstname === null || editData.DrFirstname === undefined) {
      editData.DrFirstname = '';
    }
    if (editData.DrLastname === null || editData.DrLastname === undefined) {
      editData.DrLastname = '';
    }
    if (editData.Address === null || editData.Address === undefined) {
      editData.Address = '';
    }
    if (editData.ContractAmount === null || editData.ContractAmount === undefined) {
      editData.ContractAmount = '';
    }
    
    // Auto-populate UserName with DirectorRegister or Director if UserName is empty or null
    if (!editData.UserName || editData.UserName === null || editData.UserName === undefined || editData.UserName === '') {
      // Try DirectorRegister first, then Director, then originalCustomer.DirectorRegister, then originalCustomer.Director
      const directorRegister = editData.DirectorRegister || 
                              editData.Director || 
                              originalCustomer.DirectorRegister || 
                              originalCustomer.Director || 
                              '';
      if (directorRegister) {
        console.log('🔧 Auto-populating UserName from DirectorRegister/Director:', {
          editDataDirectorRegister: editData.DirectorRegister,
          editDataDirector: editData.Director,
          originalDirectorRegister: originalCustomer.DirectorRegister,
          originalDirector: originalCustomer.Director,
          final: directorRegister
        });
        editData.UserName = directorRegister;
      } else {
        console.log('⚠️ No DirectorRegister or Director found for UserName auto-population');
        editData.UserName = '';
      }
    } else {
      console.log('✅ UserName already has value:', editData.UserName);
    }
    if (editData.Mail === null || editData.Mail === undefined) {
      editData.Mail = '';
    }
    if (editData.DigitalSignaturePassword === null || editData.DigitalSignaturePassword === undefined) {
      editData.DigitalSignaturePassword = '';
    }
    if (editData.DigitalCertFilePath === null || editData.DigitalCertFilePath === undefined) {
      editData.DigitalCertFilePath = '';
    }
    if (editData.EBarimtRegistered === null || editData.EBarimtRegistered === undefined) {
      editData.EBarimtRegistered = false;
    }
    if (editData.ContractEndDate === null || editData.ContractEndDate === undefined) {
      editData.ContractEndDate = '';
    }
    if (editData.TinCode === null || editData.TinCode === undefined) {
      editData.TinCode = '';
    }
    if (editData.ShortName === null || editData.ShortName === undefined) {
      editData.ShortName = '';
    }
    // Ensure DirectorRegister is set - use Director if DirectorRegister is missing
    if (editData.DirectorRegister === null || editData.DirectorRegister === undefined || editData.DirectorRegister === '') {
      // If DirectorRegister is empty but Director has a value, use Director
      if (editData.Director && editData.Director.trim() !== '') {
        console.log('🔧 DirectorRegister is empty, using Director value:', editData.Director);
        editData.DirectorRegister = editData.Director;
      } else {
        editData.DirectorRegister = '';
      }
    }
    if (editData.Phone === null || editData.Phone === undefined) {
      editData.Phone = '';
    }
    
    // Convert Contract_Time to ContractPeriodType for the dropdown
    if (editData.Contract_Time) {
      editData.ContractPeriodType = editData.Contract_Time === 'monthly' ? 'сар' :
                                   editData.Contract_Time === 'quarterly' ? 'улирал' :
                                   editData.Contract_Time === 'yearly' ? 'жил' : '';
    } else {
      editData.ContractPeriodType = '';
    }
    
    console.log('🔥 Edit data prepared:', editData);
    console.log('🔥 UserName in editData:', editData.UserName);
    console.log('🔥 InsuranceLoginId in editData:', editData.InsuranceLoginId);
    console.log('🔥 TaxLoginOid in editData:', editData.TaxLoginOid);
    console.log('🔥 DirectorRegister in editData:', editData.DirectorRegister);
    console.log('🔥 Director in editData:', editData.Director);
    console.log('🔥 Contract_Time:', editData.Contract_Time, 'ContractPeriodType:', editData.ContractPeriodType);
    
    console.log('🔥 Final editData before setEditRowData:', {
      TaxLoginOid: editData.TaxLoginOid,
      InsuranceLoginId: editData.InsuranceLoginId,
      CustomerName: editData.CustomerName,
      CustomerID: editData.CustomerID,
      DirectorRegister: editData.DirectorRegister,
      Director: editData.Director
    });
    
    setEditRowData(editData);
    console.log('🔥 setEditRowData called with editData.TaxLoginOid:', editData.TaxLoginOid);
    console.log('🔥 setEditRowData called with full editData:', editData);
    setEditVatPayer(originalCustomer.IsVatPayer || false);
    setEditCityPayer(originalCustomer.IsCityPayer || false);
    setActiveTab(1); // Reset to first tab when opening edit mode
    
    // Load initial subregions if customer has a RegionId
    if (originalCustomer.RegionId) {
      api.get(`/api/regions/${originalCustomer.RegionId}/subs`)
        .then(result => {
          if (result.success) setSubregions(result.data);
        })
        .catch(error => {
          console.error('Error fetching subregions:', error);
        });
    }
    
    // Populate TIN from eBarimt API if it's empty and we have a CustomerID
    if (!editData.TinCode && editData.CustomerID) {
      console.log('🔥 Populating TIN from eBarimt API for edit...');
      populateTinForEdit(editData.CustomerID);
    }
  };

  const handleEditChange = (key: string, value: string) => {
    setEditRowData(prev => ({
      ...prev,
      [key]: key === 'Active' ? value === 'true' : value,
      // Auto-fill UserName when DirectorRegister is changed
      ...(key === 'DirectorRegister' && { UserName: value }),
      // Convert ContractPeriodType to Contract_Time for backend
      ...(key === 'ContractPeriodType' && {
        Contract_Time: value === 'сар' ? 'monthly' : 
                     value === 'улирал' ? 'quarterly' : 
                     value === 'жил' ? 'yearly' : ''
      })
    }));
    
    // Auto-populate TIN when CustomerID changes
    if (key === 'CustomerID' && value && isValidRegistrationNumber(value)) {
      console.log('🔥 CustomerID changed in edit, populating TIN...');
      populateTinForEdit(value);
    }
  };


  const handleEditSave = async () => {
    if (!editingRow) return;

    // Validate required fields for edit form
    const missingFields = requiredFields.filter(field => {
      const value = editRowData[field];
      // Check for null, undefined, empty string, or whitespace-only string
      return value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '');
    });
    

    
    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map(field => fieldLabels[field]).join(', ');
      setErrorMessage(`Дараах талбаруудыг бөглөнө үү: ${missingFieldNames}`);
      setShowErrorPopup(true);
      return;
    }

    const isValidGuid = v => typeof v === 'string' && isValidGuidLib(v);
    
    // Filter out frontend-only fields that shouldn't be sent to backend
    const { ContractPeriodType, Oid, ...filteredEditData } = editRowData;
    const updateData = { ...filteredEditData, IsVatPayer: editVatPayer, IsCityPayer: editCityPayer };
    
    if (!isValidGuid(updateData.BusinessClassOid)) updateData.BusinessClassOid = null;
    if (!isValidGuid(updateData.RegionId)) updateData.RegionId = null;
    if (!isValidGuid(updateData.RegionSubId)) updateData.RegionSubId = null;
    
    // Convert GUIDs to actual string values for login IDs
    if (isValidGuid(updateData.TaxLoginOid)) {
      console.log('🔥 Converting TaxLoginOid GUID to string:', updateData.TaxLoginOid);
      let taxLogin = taxLogins.find(tl => tl.Oid === updateData.TaxLoginOid);
      if (!taxLogin && allTaxLogins.length > 0) {
        console.log('🔥 Not found in taxLogins, trying allTaxLogins for save...');
        taxLogin = allTaxLogins.find(tl => tl.Oid === updateData.TaxLoginOid);
      }
      console.log('🔥 Found tax login for save:', taxLogin);
      // Keep the Oid (GUID) for saving to database
      updateData.TaxLoginOid = taxLogin ? taxLogin.Oid : null;
      console.log('🔥 Converted TaxLoginOid for save:', updateData.TaxLoginOid);
    }
    
    // 🔧 FIXED: InsuranceLoginId now sends GUID directly (no conversion needed)
    if (isValidGuid(updateData.InsuranceLoginId)) {
      console.log('🔥 InsuranceLoginId is already a GUID, sending directly:', updateData.InsuranceLoginId);
      // No conversion needed - send GUID directly to database
    } else if (updateData.InsuranceLoginId) {
      console.log('🔥 InsuranceLoginId is not a GUID, converting to GUID:', updateData.InsuranceLoginId);
      let insuranceLogin = insuranceLogins.find(il => il.InsuranceLoginId === updateData.InsuranceLoginId);
      if (!insuranceLogin && allInsuranceLogins.length > 0) {
        console.log('🔥 Not found in insuranceLogins, trying allInsuranceLogins for save...');
        insuranceLogin = allInsuranceLogins.find(il => il.InsuranceLoginId === updateData.InsuranceLoginId);
      }
      console.log('🔥 Found insurance login for save:', insuranceLogin);
      updateData.InsuranceLoginId = insuranceLogin ? insuranceLogin.Oid : null;
      console.log('🔥 Converted InsuranceLoginId to GUID for save:', updateData.InsuranceLoginId);
    }

    // Debug log to see what's being sent for update
    console.log('🔥 Updating customer data:', updateData);
    console.log('🔥 UserName value in update:', updateData.UserName);
    console.log('🔥 InsuranceLoginId value in update:', updateData.InsuranceLoginId);
    console.log('🔥 editRowData before filtering:', editRowData);

    // UserName validation is now handled immediately when typing, so we don't need to validate here again

    // Convert empty string empId to null
    if (updateData.empId === '' || (typeof updateData.empId === 'string' && updateData.empId.trim() === '')) {
      console.log('🔧 Converting empty empId string to null');
      updateData.empId = null;
    }

    Object.keys(updateData).forEach(key => {
      if (updateData[key] === null || updateData[key] === undefined) {
        if (key === 'Active') {
          updateData[key] = false;
        } else if (key === 'empId') {
          // Keep empId as null if it's null or undefined
          updateData[key] = null;
        } else if (key === 'vatpayerRegisteredDate') {
          // Keep vatpayerRegisteredDate as empty string if null/undefined (don't convert to empty string)
          updateData[key] = '';
        } else {
          updateData[key] = '';
        }
      } else if (key === 'vatpayerRegisteredDate' && updateData[key] === '') {
        // Keep empty string for vatpayerRegisteredDate (don't convert to null)
        updateData[key] = '';
      }
      // Fix DrFirstname and DrLastname if they are arrays
      if (key === 'DrFirstname' && Array.isArray(updateData[key])) {
        updateData[key] = updateData[key][0] || '';
      }
      if (key === 'DrLastname' && Array.isArray(updateData[key])) {
        updateData[key] = updateData[key][0] || '';
      }
    });

    try {
      // Use update-from-employees endpoint with CustomerOid query parameter
      if (!editingRow.Oid) {
        setErrorMessage('Customer Oid олдсонгүй');
        setShowErrorPopup(true);
        return;
      }
      
      console.log('🔥 Using update-from-employees endpoint with CustomerOid:', editingRow.Oid);
      console.log('🔥 Update data:', updateData);
      console.log('🔥 Employees data:', employees);
      
      const result = await api.updateCustomerFromEmployees(editingRow.Oid, {
        customer: updateData,
        employees: employees || []
      });
      
      if (result.success) {
        setSuccessMessage('Харилцагч амжилттай хадгалагдлаа!');
        setShowSuccessPopup(true);
        setEditingRow(null);
        setEditRowData({});
        // Clear employees state to prevent security breach
        setEmployees([]);
        setLoadingEmployees(false);
        fetchCustomers();
      } else {
        setErrorMessage('Failed to update customer: ' + (result.error || 'Unknown error'));
        setShowErrorPopup(true);
      }
    } catch (err) {
      setErrorMessage('Failed to update customer: ' + err.message);
      setShowErrorPopup(true);
    }
  };

  const handleEditCancel = () => {
    setEditingRow(null);
    setEditRowData({});
    setEditVatPayer(false);
    setEditCityPayer(false);
    setActiveTab(1); // Reset to first tab when closing
    // Clear employees state to prevent security breach
    setEmployees([]);
    setLoadingEmployees(false);
  };

  // Tab navigation functions
  // Format number with ##.###.00 format (auto-add .00 for whole numbers)
  const formatNumber = (value: number | null): string => {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-US');
  };

  // Parse formatted number back to number
  const parseFormattedNumber = (formattedValue: string): number | null => {
    if (!formattedValue) return null;
    // Remove all non-digit characters except decimal point
    const cleanValue = formattedValue.replace(/[^\d.]/g, '');
    const number = parseFloat(cleanValue);
    return isNaN(number) ? null : number;
  };

  const getTabIndex = (fieldKey: string) => {
    // Visual layout order: left to right, top to bottom (matches includedFields array)
    const fieldOrder = [
      // Row 1: Код *, Нэр *, ТИН
      'CustomerID', 'CustomerName', 'TinCode',
      // Row 2: Захирлын регистрийн дугаар *, Захирлын нэр *, Захирлын овог *
      'DirectorRegister', 'DrFirstname', 'DrLastname',
      // Row 3: Бизнес ангилал *, Аймаг/Хот *, Сум/Дүүрэг *
      'BusinessClassOid', 'RegionId', 'RegionSubId',
      // Row 4: Хаяг, Утас *, И-мэйл
      'Address', 'Phone', 'Mail',
      // Row 5: Гэрээний дүн *, Гэрээ дуусах огноо, Англи нэр
      'ContractAmount', 'ContractEndDate', 'ShortName',
      // Row 6: ТГҮ нууц үг, ТГҮ файл оруулах, И-Баримт бүртгэл
      'DigitalSignaturePassword', 'DigitalCertFilePath', 'EBarimtRegistered',
      // Row 7: Татварын нэвтрэх эрх, НД нэвтрэх эрх, Арр хэрэглэгчийн нэр *
      'TaxLoginOid', 'InsuranceLoginId', 'UserName'
    ];
    const index = fieldOrder.indexOf(fieldKey);
    return index >= 0 ? index + 1 : undefined;
  };
  
  const handleTabNavigation = (e: React.KeyboardEvent, currentTabIndex: number | undefined, isAutoFilledField: boolean = false) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      
      // If EBarimt is loading, skip auto-filled fields
      if (isLoadingEBarimt && isAutoFilledField) {
        const nextTabIndex = (currentTabIndex || 0) + 1;
        const nextField = document.querySelector(`input[tabindex="${nextTabIndex}"], select[tabindex="${nextTabIndex}"]`) as HTMLElement;
        if (nextField && !nextField.hasAttribute('disabled') && !nextField.hasAttribute('readonly')) {
          nextField.focus();
        } else {
          const saveButton = document.querySelector('button[tabindex="22"]') as HTMLElement;
          if (saveButton) {
            saveButton.focus();
          }
        }
        return;
      }
      
      // Find next available field - focus on ALL editable fields (with or without asterisks)
      const nextTabIndex = (currentTabIndex || 0) + 1;
      
      // Search for the next editable field
      for (let i = nextTabIndex; i <= 21; i++) {
        const field = document.querySelector(`input[tabindex="${i}"], select[tabindex="${i}"]`) as HTMLElement;
        if (field && !field.hasAttribute('disabled') && !field.hasAttribute('readonly')) {
          field.focus();
          return;
        }
      }
      
      // If no more editable fields found, go to save button
      const saveButton = document.querySelector('button[tabindex="22"]') as HTMLElement;
      if (saveButton) {
        saveButton.focus();
      }
    }
  };

  const handleRowDelete = async (customer: Customer) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    // Find the original customer data with GUIDs
    const originalCustomer = customers.find(c => c.Oid === customer.Oid);
    if (!originalCustomer) return;

    try {
              const result = await api.deleteCustomer(originalCustomer.Oid);
      if (result.success) {
        fetchCustomers();
      } else {
        setErrorMessage('Failed to delete customer: ' + (result.error || 'Unknown error'));
        setShowErrorPopup(true);
      }
    } catch (err) {
      setErrorMessage('Failed to delete customer: ' + err.message);
      setShowErrorPopup(true);
    }
  };

  const handleAddTaxLogin = async () => {
    if (!newTaxLoginOid || !newTaxLoginPassword) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      setErrorMessage('Accountant ID not found. Please log in again.');
      setShowErrorPopup(true);
      return;
    }
    
        try {
      const json = await api.createTaxLogin({
        AccountantOid: currentAccountantOid,
        TaxUsername: newTaxLoginOid,
        Password: newTaxLoginPassword
      });
      
      if (json.success) {
        setShowAddTaxLogin(false);
        setNewTaxLoginOid('');
        setNewTaxLoginPassword('');
        // Show success popup
        setSuccessMessage('Татварын нэвтрэх эрх амжилттай нэмэгдлээ');
        setShowSuccessPopup(true);
        // Refresh tax logins
        try {
          const refreshResult = await api.getTaxLogins();
          if (refreshResult.success) setTaxLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh tax logins:', refreshError);
        }
      } else {
        setErrorMessage('Татварын нэвтрэх эрх нэмэхэд алдаа гарлаа: ' + (json.message || 'Unknown error'));
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Tax login creation error:', error);
      setErrorMessage('Татварын нэвтрэх эрх нэмэхэд алдаа гарлаа: ' + (error.message || 'Unknown error'));
      setShowErrorPopup(true);
    }
  };

  const handleAddInsuranceLogin = async () => {
    if (!newInsuranceLoginId || !newInsuranceLoginPassword) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      setErrorMessage('Accountant ID not found. Please log in again.');
      setShowErrorPopup(true);
      return;
    }
    
        try {
      const json = await api.createInsuranceLogin({
        AccountantOid: currentAccountantOid,
        InsuranceLoginId: newInsuranceLoginId,
        Password: newInsuranceLoginPassword
      });
      
      if (json.success) {
        setShowAddInsuranceLogin(false);
        setNewInsuranceLoginId('');
        setNewInsuranceLoginPassword('');
        // Show success popup
        setSuccessMessage('НД нэвтрэх эрх амжилттай нэмэгдлээ');
        setShowSuccessPopup(true);
        // Refresh insurance logins
        try {
          const refreshResult = await api.getInsuranceLogins(currentAccountantOid);
          if (refreshResult.success) setInsuranceLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh insurance logins:', refreshError);
        }
      } else {
        setErrorMessage('НД нэвтрэх эрх нэмэхэд алдаа гарлаа: ' + (json.message || 'Unknown error'));
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Insurance login creation error:', error);
      setErrorMessage('НД нэвтрэх эрх нэмэхэд алдаа гарлаа: ' + (error.message || 'Unknown error'));
      setShowErrorPopup(true);
    }
  };

  // Edit and delete handlers for tax login
  const handleEditTaxLogin = (taxLogin: any) => {
    setSelectedTaxLogin(taxLogin);
    setEditTaxUsername(taxLogin.TaxUsername);
    setEditTaxLoginPassword(taxLogin.Password);
    setShowEditTaxLogin(true);
  };

  const handleSaveEditTaxLogin = async () => {
    if (!selectedTaxLogin || !editTaxUsername || !editTaxLoginPassword) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      alert('Accountant ID not found. Please log in again.');
      return;
    }
    
    try {
      const json = await api.updateTaxLogin(selectedTaxLogin.Oid, {
        TaxUsername: editTaxUsername,
        Password: editTaxLoginPassword
      });
      
      if (json.success) {
        setShowEditTaxLogin(false);
        setSelectedTaxLogin(null);
        setEditTaxUsername('');
        setEditTaxLoginPassword('');
        // Refresh tax logins
        try {
          const refreshResult = await api.getTaxLogins();
          if (refreshResult.success) setTaxLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh tax logins:', refreshError);
        }
      } else {
        alert('Failed to edit tax login: ' + (json.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Tax login update error:', error);
      alert('Failed to edit tax login: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteTaxLogin = (taxLogin: any) => {
    setSelectedTaxLogin(taxLogin);
    setShowDeleteTaxLogin(true);
  };

  const handleConfirmDeleteTaxLogin = async () => {
    if (!selectedTaxLogin) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      alert('Accountant ID not found. Please log in again.');
      return;
    }
    
        try {
      const json = await api.deleteTaxLogin(selectedTaxLogin.Oid);
      
      if (json.success) {
        setShowDeleteTaxLogin(false);
        setSelectedTaxLogin(null);
        // Refresh tax logins
        try {
          const refreshResult = await api.getTaxLogins();
          if (refreshResult.success) setTaxLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh tax logins:', refreshError);
        }
      } else {
        alert('Failed to delete tax login: ' + (json.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Tax login delete error:', error);
      alert('Failed to delete tax login: ' + (error.message || 'Unknown error'));
    }
  };

  // Edit and delete handlers for insurance login
  const handleEditInsuranceLogin = (insuranceLogin: any) => {
    setSelectedInsuranceLogin(insuranceLogin);
    setEditInsuranceLoginId(insuranceLogin.InsuranceLoginId);
    setEditInsuranceLoginPassword(insuranceLogin.Password);
    setShowEditInsuranceLogin(true);
  };

  const handleSaveEditInsuranceLogin = async () => {
    if (!selectedInsuranceLogin || !editInsuranceLoginId || !editInsuranceLoginPassword) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      alert('Accountant ID not found. Please log in again.');
      return;
    }
    
        try {
      const json = await api.updateInsuranceLogin(selectedInsuranceLogin.Oid, {
        InsuranceLoginId: editInsuranceLoginId,
        Password: editInsuranceLoginPassword
      });
      
      if (json.success) {
        setShowEditInsuranceLogin(false);
        setSelectedInsuranceLogin(null);
        setEditInsuranceLoginId('');
        setEditInsuranceLoginPassword('');
        // Refresh insurance logins
        try {
          const refreshResult = await api.getInsuranceLogins(currentAccountantOid);
          if (refreshResult.success) setInsuranceLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh insurance logins:', refreshError);
        }
      } else {
        alert('Failed to edit insurance login: ' + (json.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Insurance login update error:', error);
      alert('Failed to edit insurance login: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteInsuranceLogin = (insuranceLogin: any) => {
    setSelectedInsuranceLogin(insuranceLogin);
    setShowDeleteInsuranceLogin(true);
  };

  const handleConfirmDeleteInsuranceLogin = async () => {
    if (!selectedInsuranceLogin) return;
    const currentAccountantOid = localStorage.getItem('accountantOid');
    if (!currentAccountantOid) {
      alert('Accountant ID not found. Please log in again.');
      return;
    }
    
        try {
      const json = await api.deleteInsuranceLogin(selectedInsuranceLogin.Oid);
      
      if (json.success) {
        setShowDeleteInsuranceLogin(false);
        setSelectedInsuranceLogin(null);
        // Refresh insurance logins
        try {
          const refreshResult = await api.getInsuranceLogins(currentAccountantOid);
          if (refreshResult.success) setInsuranceLogins(refreshResult.data);
        } catch (refreshError) {
          console.error('Failed to refresh insurance logins:', refreshError);
        }
      } else {
        alert('Failed to delete insurance login: ' + (json.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Insurance login delete error:', error);
      alert('Failed to delete insurance login: ' + (error.message || 'Unknown error'));
    }
  };

  // Debounced conversion function to allow both characters to be typed
  const debouncedConvertToMongolian = useCallback(
    debounce((value: string, setter: (value: string) => void) => {
      console.log('🔥 Debounced conversion called with:', value);
      
      // Check for English letters BEFORE any conversion
      if (hasConvertibleEnglishLetters(value)) {
        console.log('🔥 Debounced conversion - has convertible letters, converting...');
        const convertedValue = convertRegistrationNumber(value);
        console.log('🔥 Debounced conversion - after conversion:', convertedValue);
        
        // Convert to uppercase after Mongolian conversion
        const finalValue = convertedValue.toUpperCase();
        console.log('🔥 Debounced conversion - after uppercase:', finalValue);
        
        setter(finalValue);
      } else {
        // If no English letters, just convert to uppercase
        const finalValue = value.toUpperCase();
        setter(finalValue);
      }
    }, 300),
    []
  );

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Харилцагчдын жагсаалт</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={true}
            addDisabled={true}
            onAdd={() => setAdding(true)}
          />
        </div>

        <SmartTable
          data={transformCustomerData(customers)}
          columns={defaultColumns}
          fetchData={fetchCustomers}
          settingsKey="hariltsagch-customers-table"
          editable={true}
          addable={false}
          rowKey="Oid"
          showColumnSearch={showColumnSearch}
          showColumnSettings={showColumnSettings}
          onEdit={handleEditClick}
          onDelete={handleRowDelete}
                          onAdd={() => {
                  setAdding(true);
                  // Clear employees state to prevent security breach
                  setEmployees([]);
                  setLoadingEmployees(false);
                  // Clear form state for new customer
                  setForm({
                    CustomerID: '',
                    Director: '',
                    CustomerName: '',
                    RegionSubId: '',
                    DirectorRegister: '',
                    DrFirstname: '',
                    DrLastname: '',
                    TaxName: '',
                    TinCode: '',
                    ShortName: '',
                    BusinessClassOid: '',                   
                    Phone: '',
                    Mail: '',
                    Active: true,
                    DigitalSignaturePassword: '',
                    DigitalCertFilePath: '',
                    EBarimtRegistered: null,
                    RegionId: null,
                    Address: null,
                    ContractAmount: null,
                    ContractPeriodType: '',
                    Contract_Time: '',
                    ContractEndDate: getNextMonth5th(),
                    UserName: '',
                    IsVatPayer: false,
                    InsuranceLoginCode: '',
                    InsuranceLoginPassword: '',
                    TaxLoginOid: '',
                    CreatedDate: null,
                    // Additional fields for tab 2
                    Website: '',
                    Fax: '',
                    BankAccount: '',
                    Notes: '',
                    AuthorizedRepresentative: '',
                    ContactPerson: ''
                  });
                  setVatPayer(false);
                  setSelectedCertFile(null);
                }}
        />

        {adding && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-4 w-full max-w-3xl h-[85vh] flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  {isReactivating ? 'Харилцагч дахин идэвхжүүлэх' : 'Шинэ харилцагч нэмэх'}
                </h2>
                <button
                  onClick={() => {
                    setAdding(false);
                    setActiveTab(1); // Reset to first tab when closing
                    // Clear employees state to prevent security breach
                    setEmployees([]);
                    setLoadingEmployees(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex items-center justify-between border-b border-gray-200 mb-6">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab(1)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        setActiveTab(2);
                      }
                    }}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                      activeTab === 1
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📋 Үндсэн мэдээлэл
                  </button>
                  <button
                    onClick={() => {
                      // Check if we're adding a new customer (not editing)
                      if (!editingRow) {
                        // Show error message for new customer
                        setErrorMessage('Эхлээд харилцагчийн үндсэн мэдээллийг хадгална уу');
                        setShowErrorPopup(true);
                        return;
                      }
                      setActiveTab(2);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        setActiveTab(1);
                      }
                    }}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                      activeTab === 2
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : !editingRow 
                          ? 'border-transparent text-gray-400 cursor-not-allowed bg-gray-100'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    disabled={!editingRow}
                    title={!editingRow ? 'Эхлээд харилцагчийн үндсэн мэдээллийг хадгална уу' : ''}
                  >
                    👥 Ажилчдын жагсаалт {!editingRow && '(Хадгалах шаардлагатай)'}
                  </button>
                </div>
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {activeTab} / 2
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {includedFields.map(key => {
                    if (key === 'RegionId') {
                      const tabIndex = getTabIndex(key);
                      return (
                        <div key={key}>
                          {renderLabel(key)}
                                                  <SearchableDropdown
                          options={regions.map(region => ({ value: region.Oid, label: region.Name || region.RegionName }))}
                          value={form.RegionId || ''}
                          onChange={(value) => setForm({ ...form, RegionId: value, RegionSubId: '' })}
                          tabIndex={tabIndex}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          enableAutoComplete={true}
                        />
                        </div>
                      );
                    }
                  if (key === 'RegionSubId') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <SearchableDropdown
                          options={subregions.map(sub => ({ value: sub.Oid, label: sub.SubRegionName || sub.Name }))}
                          value={form.RegionSubId || ''}
                          onChange={(value) => setForm({ ...form, RegionSubId: value })}
                          disabled={!form.RegionId}
                          tabIndex={tabIndex}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          enableAutoComplete={true}
                        />
                      </div>
                    );
                  }
                  if (key === 'Address') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="text"
                          tabIndex={tabIndex}
                          maxLength={300}
                          value={form.Address || ''}
                          onChange={e => setForm({ ...form, Address: e.target.value })}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    );
                  }
                  if (key === 'Phone') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="text"
                          tabIndex={tabIndex}
                          maxLength={100}
                          value={form.Phone || ''}
                          onChange={e => setForm({ ...form, Phone: e.target.value })}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    );
                  }
                  if (key === 'Mail') {
                    const tabIndex = getTabIndex(key);
                    const isValidEmail = form.Mail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Mail);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="email"
                          tabIndex={tabIndex}
                          value={form.Mail || ''}
                          onChange={e => setForm({ ...form, Mail: e.target.value })}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${form.Mail && !isValidEmail ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {form.Mail && !isValidEmail && (
                          <span className="text-xs text-red-500">Зөв имэйл хаяг оруулна уу (жишээ: user@gmail.com)</span>
                        )}
                      </div>
                    );
                  }
                  if (key === 'DigitalSignaturePassword') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="text"
                          tabIndex={tabIndex}
                          maxLength={200}
                          value={form.DigitalSignaturePassword || ''}
                          onChange={e => setForm({ ...form, DigitalSignaturePassword: e.target.value })}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    );
                  }
                  if (key === 'DigitalCertFilePath') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="file"
                          tabIndex={tabIndex}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                          onChange={e => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              setSelectedCertFile(file);
                              setForm({ ...form, DigitalCertFilePath: file.name });
                            }
                          }}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                        />
                        {form.DigitalCertFilePath && (
                          <div className="text-xs text-gray-500 mt-1">Сонгосон файл: {form.DigitalCertFilePath}</div>
                        )}
                      </div>
                    );
                  }
                  if (key === 'TaxLoginOid') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key} className="min-w-0">
                        {renderLabel(key)}
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="relative flex-1 min-w-0">
                          <select
                            tabIndex={tabIndex}
                    className={`flex-1 min-w-0 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                      isAutoFetching 
                        ? 'border-green-400 bg-green-50' 
                        : isAutoPopulatingTaxLogin 
                          ? 'border-blue-400 bg-blue-50' 
                          : 'border-gray-300'
                    }`}
                            value={form.TaxLoginOid || ''}
                              key={`api/tax-login-${form.TaxLoginOid || 'empty'}`}
                            onChange={e => {
                                console.log('🔄 Татварын нэвтрэх эрх солигдож байна - сонгосон утга:', e.target.value);
                                console.log('🔄 Одоогийн form.TaxLoginOid:', form.TaxLoginOid);
                              setForm({ ...form, TaxLoginOid: e.target.value });
                                console.log('🔄 Шинэ form.TaxLoginOid болно:', e.target.value);
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                              disabled={isAutoPopulatingTaxLogin || isAutoFetching}
                          >
                            <option value="">Сонгох</option>
                            {taxLogins.map(tl => {
                              console.log('🔍 Dropdown option:', { id: tl.Oid, name: tl.TaxUsername, selected: form.TaxLoginOid === tl.Oid });
                              return (
                              <option key={tl.Oid} value={tl.Oid}>{tl.TaxUsername} ({tl.Password})</option>
                              );
                            })}
                          </select>
                {(isAutoPopulatingTaxLogin || isAutoFetching) && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${
                      isAutoFetching ? 'border-green-500' : 'border-blue-500'
                    }`}></div>
                  </div>
                )}
                        </div>
                          <button
                            type="button"
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                            onClick={() => setShowAddTaxLogin(true)}
                            title="Татварын нэвтрэх эрх нэмэх"
                          >
                            +
                          </button>
                          {form.TaxLoginOid && (
                            <>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = taxLogins.find(tl => tl.Oid === form.TaxLoginOid);
                                  if (selected) handleEditTaxLogin(selected);
                                }}
                                title="Татварын нэвтрэх эрх засах"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = taxLogins.find(tl => tl.Oid === form.TaxLoginOid);
                                  if (selected) handleDeleteTaxLogin(selected);
                                }}
                                title="Татварын нэвтрэх эрх устгах"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (key === 'InsuranceLoginId') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key} className="min-w-0">
                        {renderLabel(key)}
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <select
                              tabIndex={tabIndex}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                              value={form.InsuranceLoginId || ''}
                              onChange={async (e) => {
                                const selectedValue = e.target.value;
                                console.log('🔄 InsuranceLoginId onChange: selectedValue:', selectedValue);
                                console.log('🔄 InsuranceLoginId onChange: form.CustomerID:', form.CustomerID);
                                setForm({ ...form, InsuranceLoginId: selectedValue });
                                
                                // Clear employees when insurance login changes
                                setEmployees([]);
                                
                                // 🔒 SECURITY: Removed auto-sync to prevent security breach
                                // Users must manually trigger sync after customer is saved
                              }}
                              onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                            >
                              <option value="">Сонгох</option>
                              {insuranceLogins.map(il => (
                                <option key={il.Oid} value={il.Oid}>{il.InsuranceLoginId} ({il.Password})</option>
                              ))}
                            </select>

                          </div>
                          <button
                            type="button"
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                            onClick={() => setShowAddInsuranceLogin(true)}
                            title="НД нэвтрэх эрх нэмэх"
                          >
                            +
                          </button>
                          {form.InsuranceLoginId && (
                            <>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = insuranceLogins.find(il => il.Oid === form.InsuranceLoginId);
                                  if (selected) handleEditInsuranceLogin(selected);
                                }}
                                title="НД нэвтрэх эрх засах"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = insuranceLogins.find(il => il.Oid === form.InsuranceLoginId);
                                  if (selected) handleDeleteInsuranceLogin(selected);
                                }}
                                title="НД нэвтрэх эрх устгах"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (key === 'BusinessClassOid') {
                    const tabIndex = getTabIndex(key);
                    const bcLabel =
                      businessClasses.find(bc => bc.Oid === form.BusinessClassOid)?.BusinessClassName ||
                      businessClasses.find(bc => bc.Oid === form.BusinessClassOid)?.Name;
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <SearchableDropdown
                          options={businessClasses.map(bc => ({ value: bc.Oid, label: bc.BusinessClassName || bc.Name }))}
                          value={form.BusinessClassOid || ''}
                          valueLabel={bcLabel}
                          onChange={(value) => setForm({ ...form, BusinessClassOid: value })}
                          tabIndex={tabIndex}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          enableAutoComplete={true}
                        />
                      </div>
                    );
                  }
                  if (key === 'ContractAmount') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            tabIndex={tabIndex}
                            value={formatNumber(form.ContractAmount)}
                            onChange={e => {
                              const rawValue = e.target.value.replace(/[^\d]/g, '');
                              if (rawValue === '') {
                                setForm({ ...form, ContractAmount: null });
                              } else {
                                const number = parseInt(rawValue);
                                if (!isNaN(number)) {
                                  setForm({ ...form, ContractAmount: number });
                                }
                              }
                            }}
                            onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                            className="w-40 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            placeholder="Дүн"
                          />
                          <select
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition h-[30px] w-16"
                            value={form.ContractPeriodType || ''}
                            onChange={e => {
                              const value = e.target.value;
                              setForm({ 
                                ...form, 
                                ContractPeriodType: value,
                                Contract_Time: value === 'сар' ? 'monthly' : 
                                               value === 'улирал' ? 'quarterly' : 
                                               value === 'жил' ? 'yearly' : ''
                              });
                            }}
                          >
                            <option value="">Сонгох</option>
                            <option value="сар">Сар</option>
                            <option value="улирал">Улирал</option>
                            <option value="жил">Жил</option>
                          </select>
                        </div>
                      </div>
                    );
                  }
                  if (key === 'ContractEndDate') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="date"
                          tabIndex={tabIndex}
                          value={form.ContractEndDate ? form.ContractEndDate.substring(0, 10) : ''}
                          onChange={e => setForm({ ...form, ContractEndDate: e.target.value === '' ? null : e.target.value })}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          onClick={(e) => {
                            // Open the date picker when clicking anywhere in the field
                            e.currentTarget.showPicker();
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition cursor-pointer"
                        />
                      </div>
                    );
                  }
                  if (key === 'TaxLoginOid') {
                    return (
                      <div key={key} className="min-w-0">
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <div className="flex items-center gap-2 min-w-0">
                          <select
                            className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            value={form.TaxLoginOid || ''}
                            onChange={e => setForm({ ...form, TaxLoginOid: e.target.value })}
                          >
                            <option value="">Сонгох</option>
                            {taxLogins.map(tl => (
                              <option key={tl.Oid} value={tl.Oid}>{tl.TaxUsername} ({tl.Password})</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="bg-blue-500 text-white rounded px-2 py-1 text-lg font-bold hover:bg-blue-600 h-full"
                            onClick={() => setShowAddTaxLogin(true)}
                            title="Татварын нэвтрэх эрх нэмэх"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  }
                  if (key === 'EBarimtRegistered') {
                    const tabIndex = getTabIndex(key);
                    // Check if user tried to type letters (by comparing original input with filtered result)
                    const hasLetters = ebarimtOriginalInput !== form.EBarimtRegistered && ebarimtOriginalInput.length > 0;
                    
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="text"
                          tabIndex={tabIndex}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form.EBarimtRegistered || ''}
                          onChange={e => {
                            const value = e.target.value;
                            setEbarimtOriginalInput(value); // Store the original input
                            // Only allow digits - filter out letters immediately
                            const digitsOnly = value.replace(/[^0-9]/g, '');
                            setForm({ ...form, EBarimtRegistered: digitsOnly });
                          }}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                            hasLetters 
                              ? 'border-red-500 focus:ring-red-400 bg-red-50' 
                              : 'border-gray-300 focus:ring-blue-400'
                          }`}
                        />
                        {hasLetters && (
                          <p className="text-red-500 text-xs mt-1">Зөвхөн тоо оруулна уу</p>
                        )}
                      </div>
                    );
                  }
                  if (key === 'CustomerID') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              tabIndex={1}
                              value={form.CustomerID || ''}
                              className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                                isAutoFetching 
                                  ? 'border-green-400 bg-green-50 focus:ring-green-400' 
                                  : registerNoError 
                                    ? 'border-red-500 focus:ring-red-400' 
                                    : 'border-gray-300 focus:ring-blue-400'
                              }`}
                              onChange={e => {
                                const value = e.target.value;
                                
                                console.log('🔥 CustomerID onChange - original value:', value);
                                
                                // Set the raw value immediately for responsive UI
                                setForm({ ...form, CustomerID: value });
                                
                                // Clear error when user starts typing
                                if (value === '') {
                                  setRegisterNoError('');
                                  setIsReactivating(false);
                                  return;
                                }
                                
                                // Use debounced conversion to allow both characters to be typed
                                debouncedConvertToMongolian(value, (convertedValue) => {
                                  setForm(prev => ({ ...prev, CustomerID: convertedValue }));
                                  
                                  // Trigger debounced API check with converted value
                                  debouncedCheckTIN(convertedValue);
                                });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                  e.preventDefault();
                                  
                                  // If EBarimt is loading, skip to ShortName field (tabindex 15) to avoid auto-filled fields
                                  if (isLoadingEBarimt) {
                                    const shortNameField = document.querySelector('input[tabindex="15"]') as HTMLElement;
                                    if (shortNameField) {
                                      shortNameField.focus();
                                    }
                                    return;
                                  }
                                  
                                  // Find next available field
                                  const nextField = document.querySelector('input[tabindex="2"], select[tabindex="2"]') as HTMLElement;
                                  if (nextField && !nextField.hasAttribute('disabled')) {
                                    nextField.focus();
                                  }
                                }
                              }}
                            />
                            {(isCheckingRegisterNo || isLoadingEBarimt) && (
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              console.log('🔍 Мэдээлэл татах товч дарагдалаа!');
                              console.log('🔍 Form state:', form);
                              const customerID = form.CustomerID?.trim();
                              console.log('🔍 CustomerID:', customerID);
                              console.log('🔍 CustomerID length:', customerID?.length);
                              
                              // TaxLoginOid шалгах логик
                              if (!form.TaxLoginOid || form.TaxLoginOid.trim() === '') {
                                console.log('⚠️ TaxLoginOid сонгогдоогүй байна - анхааруулга');
                                setWarningMessage('Эхлээд "Татварын нэвтрэх эрх" талбараас сонголт хийж, дараа нь мэдээлэл татах товчлуур дарна уу');
                                setShowWarningPopup(true);
                                return;
                              }
                              
                              console.log('✅ TaxLoginOid сонгогдсон байна:', form.TaxLoginOid);
                              
                              // Код шалгах логик: кирилл үсгээр эхэлбэл 10 орон, тоогоор эхэлбэл 7 орон
                              const isCyrillic = /^[а-яёА-ЯЁ]/.test(customerID);
                              const requiredLength = isCyrillic ? 10 : 7;
                              console.log('🔍 isCyrillic:', isCyrillic);
                              console.log('🔍 requiredLength:', requiredLength);
                              
                              if (!customerID || customerID.length < requiredLength) {
                                const errorMsg = isCyrillic 
                                  ? 'Кирилл үсгээр эхэлсэн код 10 орон байх ёстой' 
                                  : 'Тоогоор эхэлсэн код 7 орон байх ёстой';
                                console.log('❌ Код шалгах алдаа:', errorMsg);
                                setErrorMessage(errorMsg);
                                setShowErrorPopup(true);
                                return;
                              }
                              
                              console.log('✅ Код шалгах амжилттай, EBarimt API дуудаж эхлэх...');
                              
                              try {
                                setIsLoadingEBarimt(true);
                                
                                // 1. Эхлээд CheckRd API дуудаж үндсэн мэдээлэл авах
                                console.log('🔍 1. CheckRd API дуудаж байна...');
                                const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:9002';
                                
                                // Get selected tax login username
                                const selectedTaxLogin = taxLogins.find(tl => tl.Oid === form.TaxLoginOid);
                                if (!selectedTaxLogin) {
                                  setErrorMessage('Татварын нэвтрэх эрх сонгогдоогүй байна');
                                  setShowErrorPopup(true);
                                  setIsLoadingEBarimt(false);
                                  return;
                                }
                                
                                const taxloginParam = encodeURIComponent(selectedTaxLogin.TaxUsername);
                                // customerOid-г олох (editingRow эсвэл form.Oid-аас)
                                const customerOid = editingRow?.Oid || form.Oid || null;
                                const customerOidParam = customerOid ? `&customerOid=${encodeURIComponent(customerOid)}` : '';
                                console.log('🔍 CheckRd API дуудаж байна, customerOid:', customerOid);
                                const ebarimtResponse = await fetch(`${API_BASE_URL}/api/CheckRd?taxlogin=${taxloginParam}&regno=${customerID}${customerOidParam}`);
                                const ebarimtResult = await ebarimtResponse.json();
                                console.log('🔍 CheckRd API хариу:', JSON.stringify(ebarimtResult, null, 2));
                                
                                if (ebarimtResult.success && ebarimtResult.data?.pin) {
                                  console.log('✅ CheckRd API-аас мэдээлэл амжилттай авлаа');
                                  
                                  const ebarimtData = ebarimtResult.data;
                                  
                                  console.log('📋 CheckRd EBarimt:', {
                                    name: ebarimtResult.name,
                                    cityPayer: ebarimtData.cityPayer,
                                    vatPayer: ebarimtData.vatPayer,
                                    found: ebarimtData.found,
                                    vatpayerRegisteredDate: ebarimtData.vatpayerRegisteredDate,
                                    isGovernment: ebarimtData.isGovernment,
                                    pin: ebarimtData.pin,
                                    entId: ebarimtData.entId,
                                  });

                                  if (ebarimtData.pin !== customerID.trim()) {
                                    setWarningMessage('Тухайн татварын эрх дээр уг харилцагч бүртгэлгүй тул татварын эрх болон кодын мэлээллээ шалгаад дахин оролдоно уу');
                                    setShowWarningPopup(true);
                                    setIsLoadingEBarimt(false);
                                    return;
                                  }

                                  const entId = ebarimtData.entId || null;
                                  const branchId = null;
                                  const gdsrNumber = ebarimtData.gdsrNumber || null;
                                  const shareHolderName = ebarimtData.shareHolderName || null;

                                  // Form-г бөглөх (CheckRd API-аас ирсэн бүх мэдээлэл)
                                  setForm(prev => ({
                                    ...prev,
                                    // EBarimt API-аас ирсэн мэдээлэл
                                    CustomerName: ebarimtResult.name || '',
                                    TinCode: ebarimtResult.tin || '',
                                    DrFirstname: shareHolderName?.split(' ')[0]?.toUpperCase() || '',
                                    DrLastname: shareHolderName?.split(' ').slice(1).join(' ')?.toUpperCase() || '',
                                    DirectorRegister: gdsrNumber?.toUpperCase() || '',
                                    UserName: gdsrNumber?.toUpperCase() || '',
                                    entId: entId || '',
                                    branchId: branchId || '',
                                    pin: ebarimtData.pin || customerID,
                                    refEntTypeCode: ebarimtData.refEntType?.code || 'Organization',
                                    IsVatPayer: ebarimtData.vatPayer === true ? 1 : 0,
                                    IsCityPayer: ebarimtData.cityPayer === true ? 1 : 0,
                                    vatpayerRegisteredDate: ebarimtData.vatpayerRegisteredDate || ''
                                  }));
                                  
                                  // Update checkbox state based on vatPayer value
                                  setVatPayer(ebarimtData.vatPayer === true);
                                  
                                  console.log('✅ Form бөглөгдлөө');
                                  setSuccessMessage('Мэдээлэл амжилттай татагдлаа');
                                  setShowSuccessPopup(true);
                                } else {
                                  console.log('❌ EBarimt API-аас мэдээлэл олдсонгүй эсвэл алдаа гарлаа');
                                  console.log('❌ ebarimtResult:', JSON.stringify(ebarimtResult, null, 2));
                                  setErrorMessage('EBarimt API-аас мэдээлэл олдсонгүй эсвэл алдаа гарлаа');
                                  setShowErrorPopup(true);
                                }
                              } catch (error) {
                                console.error('Error fetching data:', error);
                                setErrorMessage('Мэдээлэл татахад алдаа гарлаа');
                                setShowErrorPopup(true);
                              } finally {
                                setIsLoadingEBarimt(false);
                              }
                            }}
                            disabled={isLoadingEBarimt || isAutoFetching || !form.CustomerID?.trim()}
                            className={`px-3 py-1 text-xs text-white rounded transition whitespace-nowrap ${
                              isAutoFetching 
                                ? 'bg-green-500 hover:bg-green-600' 
                                : isLoadingEBarimt 
                                  ? 'bg-orange-500 hover:bg-orange-600' 
                                  : 'bg-blue-500 hover:bg-blue-600'
                            } disabled:bg-gray-300 disabled:cursor-not-allowed`}
                          >
                            {isAutoFetching ? '🔄 Автомат татах...' : isLoadingEBarimt ? '...' : 'Мэдээлэл татах'}
                          </button>
                        </div>
                        {registerNoError && (
                          <p className="text-red-500 text-xs mt-1">{registerNoError}</p>
                        )}
                      </div>
                    );
                  }
                  if (key === 'TinCode') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="text"
                          value={form.TinCode || ''}
                          disabled={true}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
                          title="ТИН нь eBarimt API-аас автоматаар татагдана"
                        />
                      </div>
                    );
                  }
                  if (key === 'DirectorRegister') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="relative">
                          <input
                            type="text"
                            tabIndex={5}
                            value={form.DirectorRegister || ''}
                            onChange={e => {
                              const value = e.target.value;
                              
                              // Convert immediately for seamless experience
                              const convertedValue = hasConvertibleEnglishLetters(value) 
                                ? convertToMongolianImmediate(value).toUpperCase()
                                : value.toUpperCase();
                              
                              // Set the converted value immediately
                              setForm(prev => ({ 
                                ...prev, 
                                DirectorRegister: convertedValue,
                                UserName: convertedValue // Auto-fill UserName with converted DirectorRegister value
                              }));
                              
                              // Clear error when user starts typing
                              if (value === '') {
                                setRegisterNoError('');
                                setIsReactivating(false);
                                return;
                              }
                              
                              // Trigger debounced API check with converted value
                              debouncedCheckDirectorRegister(convertedValue);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                e.preventDefault();
                                
                                // If EBarimt is loading, skip to Phone field (tabindex 6) to avoid auto-filled fields
                                if (isLoadingEBarimt) {
                                  const phoneField = document.querySelector('input[tabindex="6"]') as HTMLElement;
                                  if (phoneField) {
                                    phoneField.focus();
                                  }
                                  return;
                                }
                                
                                // Find next available field
                                const nextField = document.querySelector('input[tabindex="6"], select[tabindex="6"]') as HTMLElement;
                                if (nextField && !nextField.hasAttribute('disabled')) {
                                  nextField.focus();
                                }
                              }
                            }}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                              directorRegisterWarning 
                                ? 'border-red-500 focus:ring-red-400' 
                                : 'border-gray-300 focus:ring-blue-400'
                            }`}
                          />
                          {isLoadingEBarimt && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                            </div>
                          )}
                          {directorRegisterWarning && (
                            <p className="text-red-500 text-sm mt-1">{directorRegisterWarning}</p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  if (key === 'UserName') {
                    const tabIndex = getTabIndex(key);
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="text"
                          tabIndex={tabIndex}
                          value={form[key] ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm(prev => ({ ...prev, [key]: value }));
                            
                            // Validate UserName format and length based on type
                            if (value.trim()) {
                              const isIndividual = /^[А-ЯЁ]/.test(value);
                              const isOrganization = /^\d/.test(value);
                              
                              console.log('🔧 UserName validation:', {
                                value,
                                isIndividual,
                                isOrganization,
                                length: value.length
                              });
                              
                              if (isIndividual) {
                                // For individuals (Cyrillic letters): should be 10 characters
                                if (value.length === 10) {
                                  console.log('✅ Valid 10-character individual UserName');
                                  checkUserName(value.trim());
                                } else {
                                  console.log('⚠️ Invalid individual UserName length (should be 10):', value.length);
                                }
                              } else if (isOrganization) {
                                // For organizations (numbers): should be 7 digits
                                if (value.length === 7 && /^\d{7}$/.test(value)) {
                                  console.log('✅ Valid 7-digit organization UserName');
                                  checkUserName(value.trim());
                                } else {
                                  console.log('⚠️ Invalid organization UserName (should be 7 digits):', value.length, 'is numeric:', /^\d+$/.test(value));
                                }
                              } else {
                                console.log('⚠️ Invalid UserName format - should start with Cyrillic letter or number:', value);
                              }
                            }
                          }}
                          onKeyDown={(e) => handleTabNavigation(e, tabIndex, false)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    );
                  }


                  // Skip fields that don't have labels or should not be rendered as text inputs
                  if (!fieldLabels[key] || ['EBarimtRegistered', 'NDRegistered', 'TaxRegistered', 'Active', 'IsVatPayer', 'IsCityPayer', 'vatPayer', 'TaxName', 'InsuranceLoginCode', 'InsuranceLoginPassword', 'TaxLoginOid', 'Director', 'CreatedDate'].includes(key)) {
                    return null;
                  }
                  
                  // Determine tabIndex based on field order
                  const isAutoFilledField = ['CustomerName', 'TinCode', 'DrFirstname', 'DrLastname'].includes(key);
                  const tabIndex = getTabIndex(key);
                  
                  return (
                    <div key={key}>
                      {renderLabel(key)}
                      <input
                        type="text"
                        tabIndex={tabIndex}
                        value={form[key] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const newForm = { ...form, [key]: value };
                          if (key === 'CustomerID' && value !== form.CustomerID) {
                            newForm.CustomerName = '';
                            newForm.TinCode = '';
                          }
                          if (key === 'DirectorRegister' && value !== form.DirectorRegister) {
                            newForm.DrFirstname = '';
                            newForm.DrLastname = '';
                          }
                          setForm(newForm);
                        }}
                        onKeyDown={(e) => handleTabNavigation(e, tabIndex, isAutoFilledField)}
                        className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                          isAutoFilledField || isLoadingEBarimt ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        readOnly={isAutoFilledField}
                        disabled={isAutoFilledField || isLoadingEBarimt}
                      />
                    </div>
                  );
                })}
              </div>
              )}
              
              {/* Tab 2 Content */}
              {activeTab === 2 && (
                <div className="space-y-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-800">Ажилчдын жагсаалт</h3>
                    <p className="text-gray-600 text-sm">Харилцагчийн ажилчдын мэдээлэл</p>
                  </div>
                  
                  {/* Save First Message for New Customer */}
                  {!editingRow && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800">
                            Эхлээд харилцагчийн үндсэн мэдээллийг хадгална уу
                          </h3>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>Ажилчдын жагсаалтыг харахын тулд эхлээд харилцагчийн үндсэн мэдээллийг хадгалах шаардлагатай.</p>
                          </div>
                          <div className="mt-4">
                            <button
                              type="button"
                              onClick={() => setActiveTab(1)}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                            >
                              Үндсэн мэдээлэл рүү буцах
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Employees Table - Only show if customer is saved (editing mode) */}
                  {editingRow && (
                    <>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                Код
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                Нэр
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                Овог
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                ТИН
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                              Даатгуулагчийн төрөл
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                              Ажил мэргэжлийн код
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              console.log('🔄 Add Modal Table Render - loadingEmployees:', loadingEmployees);
                              console.log('🔄 Add Modal Table Render - employees:', employees);
                              console.log('🔄 Add Modal Table Render - employees.length:', employees?.length || 0);
                              console.log('🔄 Add Modal Table Render - form.InsuranceLoginId:', form.InsuranceLoginId);
                              
                              if (loadingEmployees) {
                                return (
                                  <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                      <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                                        Ажилчдын мэдээлэл татаж байна...
                                      </div>
                                    </td>
                                  </tr>
                                );
                              } else if (employees && employees.length > 0) {
                                console.log('✅ Add Modal Table Render - Rendering employees:', employees);
                                return employees.map((employee, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.Code || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.Name || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.LastName || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.TIN || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.InsureTypeCode || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                      {employee.OccupationCode || '-'}
                                    </td>

                                  </tr>
                                ));
                              } else {
                                console.log('❌ Add Modal Table Render - No employees to display');
                                return (
                                  <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                      {form.InsuranceLoginId ? 'Ажилчдын мэдээлэл олдсонгүй' : 'НД нэвтрэх эрх сонгоно уу'}
                                    </td>
                                  </tr>
                                );
                              }
                            })()}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Security Note */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <span className="text-yellow-600 text-sm">🔒</span>
                          <div className="text-sm text-yellow-800">
                            <strong>Аюулгүй байдлын шалгалт:</strong> Ажилчдын жагсаалт татахдаа НД порталаар харилцагчийн эрхийг шалгана. Зөвхөн тухайн харилцагчид хандах эрхтэй НД нэвтрэх эрхийг ашиглана уу.
                          </div>
                        </div>
                      </div>
                      
                      {/* Fetch Employees Button */}
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            // Find customerOid from customers list
                            const customer = customers.find(c => c.CustomerID === form.CustomerID);
                            if (customer?.Oid) {
                              handleOpenSalaryModal(customer.Oid);
                            }
                          }}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-green-600 hover:bg-green-700 text-white"
                        >
                          💰 Цалингийн жагсаалт
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleFetchEmployees(form.CustomerID, form.InsuranceLoginId, true, form.CustomerID, form.CustomerName)}
                          disabled={loadingEmployees}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            loadingEmployees 
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {loadingEmployees ? '⏳ Татаж байна...' : '🔄 Ажилчдын жагсаалт татах'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>
              
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={vatPayer} readOnly />
                    НӨАТ төлөгч
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={cityPayer} readOnly />
                    НХАТ төлөгч
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.Active} readOnly />
                    Идэвхтэй
                  </label>
                </div>
                <button
                  type="button"
                  tabIndex={22}
                  disabled={true}
                  className="bg-gray-300 text-gray-500 cursor-not-allowed px-6 py-2 rounded-lg shadow font-semibold flex items-center gap-2"
                >
                  <span className="text-lg">💾</span> {isReactivating ? 'Дахин идэвхжүүлэх' : 'Хадгалах'}
                </button>
              </div>
              
              {/* Харилцагч олдохгүй үед тайлбар харуулах */}
              {customerNotFoundMessage && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-yellow-600 mr-2">⚠️</span>
                    <p className="text-yellow-800 text-sm">{customerNotFoundMessage}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {editingRow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-4 w-full max-w-3xl h-[85vh] flex flex-col">
                                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">Харилцагч засах</h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleEditCancel}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                      >
                        ×
                      </button>
                    </div>
                  </div>
              
              {/* Tab Navigation for Edit */}
              <div className="flex items-center justify-between border-b border-gray-200 mb-6">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab(1)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        setActiveTab(2);
                      }
                    }}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                      activeTab === 1
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📋 Үндсэн мэдээлэл
                  </button>
                  <button
                    onClick={() => setActiveTab(2)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        setActiveTab(1);
                      }
                    }}
                    className={`px-6 py-3 font-medium text-sm border-b-2 transition-all duration-200 ${
                      activeTab === 2
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    👥 Ажилчдын жагсаалт
                  </button>
                </div>
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {activeTab} / 2
                </div>
              </div>

              {/* Tab Content for Edit */}
              <div className="flex-1 overflow-y-auto">
                {activeTab === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {editFormFields.map(key => {
                  if (key === 'Mail') {
                    const isValidEmail = editRowData.Mail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editRowData.Mail);
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="email"
                          value={editRowData.Mail || ''}
                          onChange={e => handleEditChange('Mail', e.target.value)}
                          className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${editRowData.Mail && !isValidEmail ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {editRowData.Mail && !isValidEmail && (
                          <span className="text-xs text-red-500">Зөв имэйл хаяг оруулна уу (жишээ: user@gmail.com)</span>
                        )}
                      </div>
                    );
                  }
                  if (key === 'RegionId') {
                    console.log('🔍 RegionId field rendering - regions:', regions);
                    console.log('🔍 RegionId field rendering - editRowData.RegionId:', editRowData.RegionId);
                    console.log('🔍 RegionId field rendering - regions length:', regions.length);
                    console.log('🔍 RegionId field rendering - looking for region with Oid:', editRowData.RegionId);
                    const matchingRegion = regions.find(r => r.Oid === editRowData.RegionId);
                    console.log('🔍 RegionId field rendering - matching region found:', matchingRegion);
                    if (matchingRegion) {
                      console.log('🔍 RegionId field rendering - matching region name:', matchingRegion.RegionName || matchingRegion.Name);
                    }
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <SearchableDropdown
                          options={regions.map(region => ({ value: region.Oid, label: region.RegionName || region.Name }))}
                          value={editRowData.RegionId || ''}
                          onChange={(value) => handleEditChange('RegionId', value)}
                          enableAutoComplete={true}
                        />
                      </div>
                    );
                  }
                  if (key === 'RegionSubId') {
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <SearchableDropdown
                          options={subregions.map(sub => ({ value: sub.Oid, label: sub.SubRegionName || sub.Name }))}
                          value={editRowData.RegionSubId || ''}
                          onChange={(value) => handleEditChange('RegionSubId', value)}
                          disabled={!editRowData.RegionId}
                          enableAutoComplete={true}
                        />
                      </div>
                    );
                  }
                  if (key === 'BusinessClassOid') {
                    const bcLabel =
                      businessClasses.find(bc => bc.Oid === editRowData.BusinessClassOid)?.BusinessClassName ||
                      businessClasses.find(bc => bc.Oid === editRowData.BusinessClassOid)?.Name;
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <SearchableDropdown
                          options={businessClasses.map(bc => ({ value: bc.Oid, label: bc.BusinessClassName || bc.Name }))}
                          value={editRowData.BusinessClassOid || ''}
                          valueLabel={bcLabel}
                          onChange={(value) => handleEditChange('BusinessClassOid', value)}
                          enableAutoComplete={true}
                        />
                      </div>
                    );
                  }
                  if (key === 'TaxLoginOid') {
                    console.log('🔥 TaxLoginOid field rendering - editRowData.TaxLoginOid:', editRowData.TaxLoginOid);
                    console.log('🔥 TaxLoginOid field rendering - taxLogins length:', taxLogins.length);
                    console.log('🔥 TaxLoginOid field rendering - taxLogins:', taxLogins);
                    console.log('🔥 TaxLoginOid field rendering - taxLogins Oids:', taxLogins.map(tl => tl.Oid));
                    console.log('🔥 TaxLoginOid field rendering - allTaxLogins length:', allTaxLogins.length);
                    console.log('🔥 TaxLoginOid field rendering - allTaxLogins:', allTaxLogins);
                    console.log('🔥 TaxLoginOid field rendering - allTaxLogins Oids:', allTaxLogins.map(tl => tl.Oid));
                    
                    // Check if TaxLoginOid exists in taxLogins
                    const taxLoginExists = taxLogins.some(tl => tl.Oid === editRowData.TaxLoginOid);
                    const taxLoginInAll = allTaxLogins.some(tl => tl.Oid === editRowData.TaxLoginOid);
                    console.log('🔥 TaxLoginOid field rendering - does value exist in taxLogins?', taxLoginExists);
                    console.log('🔥 TaxLoginOid field rendering - does value exist in allTaxLogins?', taxLoginInAll);
                    
                    // Find matching tax login
                    const matchingTaxLogin = taxLogins.find(tl => tl.Oid === editRowData.TaxLoginOid) || 
                                           allTaxLogins.find(tl => tl.Oid === editRowData.TaxLoginOid);
                    console.log('🔥 TaxLoginOid field rendering - matching tax login:', matchingTaxLogin);
                    
                    // Build dropdown options: use taxLogins if available, but always include the selected option if it exists
                    let dropdownOptions = taxLogins.length > 0 ? [...taxLogins] : [...allTaxLogins];
                    
                    // If the selected TaxLoginOid exists in allTaxLogins but not in current dropdownOptions, add it
                    if (editRowData.TaxLoginOid && matchingTaxLogin && !dropdownOptions.some(tl => tl.Oid === editRowData.TaxLoginOid)) {
                      dropdownOptions.push(matchingTaxLogin);
                    }
                    
                    // Remove duplicates based on Oid (in case matchingTaxLogin was already in the list)
                    const uniqueOptions = dropdownOptions.filter((tl, index, self) => 
                      index === self.findIndex(t => t.Oid === tl.Oid)
                    );
                    
                    console.log('🔥 TaxLoginOid field rendering - dropdownOptions count:', uniqueOptions.length);
                    
                    // If not found, try to find by comparing all Oids
                    if (!matchingTaxLogin && editRowData.TaxLoginOid) {
                      console.log('⚠️ TaxLoginOid not found, checking all Oids...');
                      const allOids = [...taxLogins.map(tl => tl.Oid), ...allTaxLogins.map(tl => tl.Oid)];
                      console.log('⚠️ All available TaxLogin Oids:', allOids);
                      console.log('⚠️ Looking for:', editRowData.TaxLoginOid);
                      console.log('⚠️ Exact match?', allOids.includes(editRowData.TaxLoginOid));
                      
                      // Try case-insensitive comparison
                      const caseInsensitiveMatch = [...taxLogins, ...allTaxLogins].find(tl => 
                        tl.Oid && tl.Oid.toLowerCase() === editRowData.TaxLoginOid?.toLowerCase()
                      );
                      console.log('⚠️ Case-insensitive match:', caseInsensitiveMatch);
                    }
                    
                    return (
                      <div key={key} className="min-w-0">
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <div className="flex items-center gap-1 min-w-0">
                          <select
                            className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            value={editRowData.TaxLoginOid || ''}
                            onChange={e => {
                              console.log('🔥 TaxLoginOid dropdown onChange - selected value:', e.target.value);
                              console.log('🔥 TaxLoginOid dropdown onChange - previous value:', editRowData.TaxLoginOid);
                              handleEditChange('TaxLoginOid', e.target.value);
                            }}
                          >
                            <option value="">Сонгох</option>
                            {/* Show options from taxLogins (if available), but always include the selected option */}
                            {uniqueOptions.map(tl => {
                              const isSelected = tl.Oid === editRowData.TaxLoginOid;
                              if (isSelected) {
                                console.log('✅ TaxLoginOid option found and selected:', tl);
                              }
                              return (
                                <option key={tl.Oid} value={tl.Oid}>
                                  {tl.TaxUsername} ({tl.Password})
                                </option>
                              );
                            })}
                            {/* If TaxLoginOid exists in editRowData but not in any available options, add it as a hidden option for value matching */}
                            {editRowData.TaxLoginOid && 
                             !uniqueOptions.some(tl => tl.Oid === editRowData.TaxLoginOid) && (
                              <option key={editRowData.TaxLoginOid} value={editRowData.TaxLoginOid} style={{ display: 'none' }}>
                                {editRowData.TaxLoginOid}
                              </option>
                            )}
                          </select>
                          <button
                            type="button"
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                            onClick={() => setShowAddTaxLogin(true)}
                            title="Татварын нэвтрэх эрх нэмэх"
                          >
                            +
                          </button>
                          {editRowData.TaxLoginOid && (
                            <>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = taxLogins.find(tl => tl.Oid === editRowData.TaxLoginOid);
                                  if (selected) handleEditTaxLogin(selected);
                                }}
                                title="Татварын нэвтрэх эрх засах"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = taxLogins.find(tl => tl.Oid === editRowData.TaxLoginOid);
                                  if (selected) handleDeleteTaxLogin(selected);
                                }}
                                title="Татварын нэвтрэх эрх устгах"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (key === 'InsuranceLoginId') {
                    console.log('🔥 InsuranceLoginId field rendering - editRowData.InsuranceLoginId:', editRowData.InsuranceLoginId);
                    console.log('🔥 InsuranceLoginId field rendering - insuranceLogins length:', insuranceLogins.length);
                    console.log('🔥 InsuranceLoginId field rendering - insuranceLogins:', insuranceLogins);
                    console.log('🔥 InsuranceLoginId field rendering - does value exist in options?', insuranceLogins.some(il => il.Oid === editRowData.InsuranceLoginId));
                    return (
                      <div key={key} className="min-w-0">
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <div className="flex items-center gap-1 min-w-0">
                          <select
                            className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            value={editRowData.InsuranceLoginId || ''}
                            onChange={e => {
                              console.log('🔥 InsuranceLoginId dropdown RENDER - value prop:', editRowData.InsuranceLoginId || '');
                              console.log('🔥 InsuranceLoginId dropdown RENDER - editRowData:', editRowData);
                              handleEditChange('InsuranceLoginId', e.target.value);
                            }}
                          >
                            <option value="">Сонгох</option>
                            {insuranceLogins.map(il => (
                              <option key={il.Oid} value={il.Oid}>{il.InsuranceLoginId} ({il.Password})</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                            onClick={() => setShowAddInsuranceLogin(true)}
                            title="НД нэвтрэх эрх нэмэх"
                          >
                            +
                          </button>
                          {editRowData.InsuranceLoginId && (
                            <>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = insuranceLogins.find(il => il.Oid === editRowData.InsuranceLoginId);
                                  if (selected) handleEditInsuranceLogin(selected);
                                }}
                                title="НД нэвтрэх эрх засах"
                              >
                                ✏️
                              </button>
                              <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-xs font-medium hover:text-gray-800 transition-colors"
                                onClick={() => {
                                  const selected = insuranceLogins.find(il => il.Oid === editRowData.InsuranceLoginId);
                                  if (selected) handleDeleteInsuranceLogin(selected);
                                }}
                                title="НД нэвтрэх эрх устгах"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (key === 'DigitalCertFilePath') {
                    return (
                      <div key={key}>
                        <label className="block mb-1 text-sm text-gray-700">{fieldLabels[key]}</label>
                        <input
                          type="file"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                          onChange={e => {
                            const file = e.target.files && e.target.files[0];
                            if (file) {
                              setSelectedCertFile(file);
                              handleEditChange('DigitalCertFilePath', file.name);
                            }
                          }}
                        />
                        {editRowData.DigitalCertFilePath && (
                          <div className="text-xs text-gray-500 mt-1">Сонгосон файл: {editRowData.DigitalCertFilePath}</div>
                        )}
                      </div>
                    );
                  }
                  if (key === 'CustomerID') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type="text"
                              value={editRowData.CustomerID || ''}
                              onChange={e => handleEditChange('CustomerID', e.target.value)}
                              onBlur={handleBlurTIN}
                              className="w-full px-2 py-1 border border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed rounded text-sm transition"
                              readOnly={true}
                            />
                          </div>
                          <button
                            type="button"
                            disabled={true}
                            className="px-3 py-1 text-xs text-gray-500 rounded whitespace-nowrap bg-gray-300 cursor-not-allowed"
                          >
                            Мэдээлэл татах
                          </button>
                        </div>
                      </div>
                    );
                  }
                  if (key === 'TinCode') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="text"
                          value={editRowData.TinCode || ''}
                          disabled={true}
                          className="w-full px-2 py-1 border border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed rounded text-sm"
                          title="ТИН нь eBarimt API-аас автоматаар татагдана"
                        />
                      </div>
                    );
                  }
                  if (key === 'DirectorRegister') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="relative">
                          <input
                            type="text"
                            value={editRowData.DirectorRegister || editRowData.Director || ''}
                            onChange={e => {
                              const value = e.target.value;
                              
                              // Convert immediately for seamless experience
                              const convertedValue = hasConvertibleEnglishLetters(value) 
                                ? convertToMongolianImmediate(value).toUpperCase()
                                : value.toUpperCase();
                              
                              handleEditChange('DirectorRegister', convertedValue);
                              // Call debounced function for eBarimt lookup with uppercase value
                              if (convertedValue.trim()) {
                                checkDirectorRegisterEdit(convertedValue.trim());
                              }
                            }}
                            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 transition ${
                              directorRegisterWarning 
                                ? 'border-red-500 focus:ring-red-400' 
                                : 'border-gray-300 focus:ring-blue-400'
                            }`}
                          />
                          {isLoadingDirectorRegister && (
                            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            </div>
                          )}
                          {directorRegisterWarning && (
                            <p className="text-red-500 text-sm mt-1">{directorRegisterWarning}</p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  if (key === 'UserName') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="text"
                          value={editRowData[key] ?? ''}
                          onChange={e => {
                            const value = e.target.value;
                            handleEditChange(key, value);
                            
                            // Validate UserName format and length based on type
                            if (value.trim()) {
                              const isIndividual = /^[А-ЯЁ]/.test(value);
                              const isOrganization = /^\d/.test(value);
                              
                              console.log('🔧 UserName validation (edit):', {
                                value,
                                isIndividual,
                                isOrganization,
                                length: value.length
                              });
                              
                              if (isIndividual) {
                                // For individuals (Cyrillic letters): should be 10 characters
                                if (value.length === 10) {
                                  console.log('✅ Valid 10-character individual UserName (edit)');
                                  checkUserNameEdit(value.trim());
                                } else {
                                  console.log('⚠️ Invalid individual UserName length (should be 10) in edit:', value.length);
                                }
                              } else if (isOrganization) {
                                // For organizations (numbers): should be 7 digits
                                if (value.length === 7 && /^\d{7}$/.test(value)) {
                                  console.log('✅ Valid 7-digit organization UserName (edit)');
                                  checkUserNameEdit(value.trim());
                                } else {
                                  console.log('⚠️ Invalid organization UserName (should be 7 digits) in edit:', value.length, 'is numeric:', /^\d+$/.test(value));
                                }
                              } else {
                                console.log('⚠️ Invalid UserName format in edit - should start with Cyrillic letter or number:', value);
                              }
                            }
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                        />
                      </div>
                    );
                  }
                  if (key === 'ContractAmount') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={formatNumber(editRowData.ContractAmount)}
                            onChange={e => {
                              const rawValue = e.target.value.replace(/[^\d]/g, '');
                              if (rawValue === '') {
                                handleEditChange('ContractAmount', '');
                              } else {
                                const number = parseInt(rawValue);
                                if (!isNaN(number)) {
                                  handleEditChange('ContractAmount', number.toString());
                                }
                              }
                            }}
                            className="w-40 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                            placeholder="Дүн"
                          />
                          <select
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 transition h-[30px] w-16"
                            value={editRowData.ContractPeriodType || ''}
                            onChange={e => handleEditChange('ContractPeriodType', e.target.value)}
                          >
                            <option value="">Сонгох</option>
                            <option value="сар">Сар</option>
                            <option value="улирал">Улирал</option>
                            <option value="жил">Жил</option>
                          </select>
                        </div>
                      </div>
                    );
                  }
                  if (key === 'ContractEndDate') {
                    return (
                      <div key={key}>
                        {renderLabel(key)}
                        <input
                          type="date"
                          value={editRowData.ContractEndDate ? editRowData.ContractEndDate.substring(0, 10) : ''}
                          onChange={e => handleEditChange(key, e.target.value === '' ? null : e.target.value)}
                          onClick={(e) => {
                            // Open the date picker when clicking anywhere in the field
                            e.currentTarget.showPicker();
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition cursor-pointer"
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={key}>
                      {renderLabel(key)}
                      <input
                        type="text"
                        value={editRowData[key] ?? ''}
                        onChange={e => handleEditChange(key, e.target.value)}
                        className={`w-full px-2 py-1 border rounded text-sm transition ${
                          ['Oid', 'CustomerID', 'CustomerName', 'TinCode'].includes(key)
                            ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                            : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                        }`}
                        readOnly={['Oid', 'CustomerID', 'CustomerName', 'TinCode'].includes(key)}
                      />
                    </div>
                  );
                })}
              </div>
              )}
              
              {/* Tab 2 Content for Edit */}
              {activeTab === 2 && (
                <div className="space-y-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-800">Ажилчдын жагсаалт</h3>
                    <p className="text-gray-600 text-sm">Харилцагчийн ажилчдын мэдээлэл</p>
                  </div>
                  
                  {/* Employees Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Код
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Нэр
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Овог
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            ТИН
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                            Даатгуулагчийн төрөл
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                          Ажил мэргэжлийн код
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {(() => {
                          console.log('🔄 Edit Modal Table Render - loadingEmployees:', loadingEmployees);
                          console.log('🔄 Edit Modal Table Render - employees:', employees);
                          console.log('🔄 Edit Modal Table Render - employees.length:', employees?.length || 0);
                          console.log('🔄 Edit Modal Table Render - isClearingEmployees:', isClearingEmployees);
                          console.log('🔄 Edit Modal Table Render - editRowData.InsuranceLoginId:', editRowData.InsuranceLoginId);
                          console.log('🔄 Edit Modal Table Render - employees isArray:', Array.isArray(employees));
                          
                          if (loadingEmployees) {
                            return (
                              <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                  <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
                                    Ажилчдын мэдээлэл татаж байна...
                                  </div>
                                </td>
                              </tr>
                            );
                          } else if (employees && Array.isArray(employees) && employees.length > 0 && !isClearingEmployees) {
                            console.log('✅ Edit Modal Table Render - Rendering employees:', employees);
                            console.log('✅ Edit Modal Table Render - employees array check:', {
                              isArray: Array.isArray(employees),
                              length: employees.length,
                              isClearingEmployees: isClearingEmployees,
                              firstEmployee: employees[0]
                            });
                            return employees.map((employee, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.Code || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.Name || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.LastName || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.TIN || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.InsureTypeCode || '-'}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 border-b">
                                  {employee.OccupationCode || '-'}
                                </td>

                              </tr>
                            ));
                          } else {
                            console.log('❌ Edit Modal Table Render - No employees to display');
                            return (
                              <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                                  {editRowData.InsuranceLoginId ? 'Ажилчдын мэдээлэл олдсонгүй' : 'НД нэвтрэх эрх сонгоно уу'}
                                </td>
                              </tr>
                            );
                          }
                        })()}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Fetch Employees Button */}
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        // Find customerOid from customers list
                        const customer = customers.find(c => c.CustomerID === editRowData.CustomerID);
                        if (customer?.Oid) {
                          handleOpenSalaryModal(customer.Oid);
                        }
                      }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-green-600 hover:bg-green-700 text-white"
                    >
                      💰 Цалингийн жагсаалт
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleFetchEmployeesFromEndpoint}
                      disabled={loadingEmployees}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        loadingEmployees 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {loadingEmployees ? '⏳ Татаж байна...' : '🔄 Ажилчдын жагсаалт татах'}
                    </button>
                  </div>
                </div>
              )}
              </div>
              
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={editVatPayer} 
                      readOnly
                      disabled
                      className="cursor-not-allowed"
                    />
                    НӨАТ төлөгч
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      checked={!!editRowData.Active} 
                      onChange={e => handleEditChange('Active', e.target.checked ? 'true' : 'false')}
                    />
                    Идэвхтэй
                  </label>
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 text-sm">
                    <input 
                      type="checkbox" 
                      readOnly
                      disabled
                      checked={editCityPayer}                       
                      className="cursor-not-allowed"
                    />
                    НХАТ төлөгч
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={true}
                    className="bg-gray-300 text-gray-500 cursor-not-allowed px-6 py-2 rounded-lg shadow font-semibold flex items-center gap-2"
                  >
                    <span className="text-lg">💾</span> Хадгалах
                  </button>
                  <button
                    className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded-lg shadow font-semibold flex items-center gap-2 transition"
                    onClick={handleEditCancel}
                  >
                    Болих
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Add TaxLogin Modal */}
        {showAddTaxLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">Татварын нэвтрэх эрх нэмэх</h2>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нэвтрэх нэр</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={newTaxLoginOid}
                  onChange={e => setNewTaxLoginOid(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нууц үг</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={newTaxLoginPassword}
                  onChange={e => setNewTaxLoginPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowAddTaxLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleAddTaxLogin}
                >
                  Нэмэх
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Add InsuranceLogin Modal */}
        {showAddInsuranceLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">НД нэвтрэх эрх нэмэх</h2>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нэвтрэх нэр</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={newInsuranceLoginId}
                  onChange={e => setNewInsuranceLoginId(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нууц үг</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={newInsuranceLoginPassword}
                  onChange={e => setNewInsuranceLoginPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowAddInsuranceLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleAddInsuranceLogin}
                >
                  Нэмэх
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Edit TaxLogin Modal */}
        {showEditTaxLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">Татварын нэвтрэх эрх засах</h2>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нэвтрэх нэр</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={editTaxUsername}
                  onChange={e => setEditTaxUsername(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нууц үг</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={editTaxLoginPassword}
                  onChange={e => setEditTaxLoginPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowEditTaxLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleSaveEditTaxLogin}
                >
                  Хадгалах
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Delete TaxLogin Modal */}
        {showDeleteTaxLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">Татварын нэвтрэх эрх устгах</h2>
              <p className="mb-4 text-gray-600">Устгахдаа итгэлтэй байна уу?</p>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowDeleteTaxLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                  onClick={handleConfirmDeleteTaxLogin}
                >
                  Устгах
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Edit InsuranceLogin Modal */}
        {showEditInsuranceLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">НД нэвтрэх эрх засах</h2>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нэвтрэх нэр</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={editInsuranceLoginId}
                  onChange={e => setEditInsuranceLoginId(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm mb-1">Нууц үг</label>
                <input
                  className="w-full px-2 py-1 border border-gray-300 rounded"
                  value={editInsuranceLoginPassword}
                  onChange={e => setEditInsuranceLoginPassword(e.target.value)}
                  type="password"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowEditInsuranceLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleSaveEditInsuranceLogin}
                >
                  Хадгалах
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Delete InsuranceLogin Modal */}
        {showDeleteInsuranceLogin && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-lg font-semibold mb-4">НД нэвтрэх эрх устгах</h2>
              <p className="mb-4 text-gray-600">Устгахдаа итгэлтэй байна уу?</p>
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-1 rounded bg-gray-300 hover:bg-gray-400"
                  onClick={() => setShowDeleteInsuranceLogin(false)}
                >
                  Болих
                </button>
                <button
                  className="px-4 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                  onClick={handleConfirmDeleteInsuranceLogin}
                >
                  Устгах
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Popup */}
        <SuccessPopup
          message={successMessage}
          isVisible={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          duration={3000}
        />
        
        {/* Error Popup */}
        <ErrorPopup
          message={errorMessage}
          isVisible={showErrorPopup}
          onClose={() => setShowErrorPopup(false)}
          duration={5000}
        />
        
        {/* Warning Popup */}
        <WarningPopup
          message={warningMessage}
          isVisible={showWarningPopup}
          onClose={() => setShowWarningPopup(false)}
          duration={4000}
        />
        
        {/* Salary Modal */}
        {showSalaryModal && selectedCustomerOidForSalary && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
              <button
                onClick={handleCloseSalaryModal}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold z-10"
              >
                &times;
              </button>
         
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default HariltsagchPage; 